import { useState, useEffect, useContext, useRef } from 'react';
import { AppContext } from '../../context';
import { parseRoles, canAccess } from '../../utils/rbac';
import api from '../../hooks/useApi';
import Modal from '../shared/Modal';
import { useToast } from '../ui/Toast';
import { formatDate } from '../../utils/format';
import { fmtDateTimeLong } from '../../utils/dates.js';
import { toCsv } from '../../utils/csv.js';
import PriceHistoryChart from './PriceHistoryChart';
import ProductSupplyNote from '../procurement/ProductSupplyNote';
import HowToOrder from '../shared/HowToOrder';
import CatalogueSuppliers from '../procurement/CatalogueSuppliers';
import SellPriceChart from './SellPriceChart';
import OneMediaLookup from './OneMediaLookup';
import SupplierMatchingPanel from './SupplierMatchingPanel';
import { sowPrice, autoSellPrice, commissionPctOf } from '../../utils/sellPrice.js';

const fmt = (v) => v != null ? `£${Number(v).toFixed(2)}` : '—';

const TDSYNNEX_STATUS_LABELS = {
  Active: null,
  PhasedOut: 'End of life — stock is available while it lasts, but the product is being discontinued.',
  Discontinued: 'Permanently discontinued — no further stock will be available.',
  NonStock: 'Non-stocked item — must be special ordered.',
};

const daysSince = (d) => {
  if (!d) return null;
  const days = Math.floor((Date.now() - new Date(d)) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
};
const fmtRange = (mn, mx) => {
  if (mn == null && mx == null) return '—';
  if (mn == null || mx == null || Number(mn) === Number(mx)) return fmt(mn ?? mx);
  return `${fmt(mn)} – ${fmt(mx)}`;
};
const fmtComm = (buy, sell) => {
  if (buy == null || sell == null) return '—';
  const c = Number(sell) - Number(buy);
  return <span className={c >= 0 ? 'text-emerald-600' : 'text-red-500'}>{c >= 0 ? '+' : ''}£{c.toFixed(2)}</span>;
};

// ─── Column sorting ─────────────────────────────────────────────────────────
// Each column says how to read its value and what kind of value that is, rather
// than the comparator guessing from the row. Guessing does not survive this
// table: min_price, sell_price and commission are all nullable, and a null read
// as '' makes a numeric column compare as text, which puts 100 before 12.
// SOW Price and Commission sort on the price shown, automatic or typed, so they
// take the default commission % as well as the row.
const SORT_COLUMNS = {
  code:          { get: p => p.code,                type: 'text'   },
  description:   { get: p => p.description,         type: 'text'   },
  manufacturer:  { get: p => p.manufacturer,        type: 'text'   },
  category:      { get: p => p.category,            type: 'text'   },
  // Price Range shows min – max; the cheapest source is what people compare on.
  min_price:     { get: p => p.min_price,           type: 'number' },
  sell_price:    { get: (p, pct) => sowPrice(p, pct).price, type: 'number' },
  commission:    { get: (p, pct) => { const { price } = sowPrice(p, pct); return (p.min_price == null || price == null) ? null : price - Number(p.min_price); }, type: 'number' },
  price_updated: { get: p => p.price_last_modified, type: 'text'   },
  last_ordered:  { get: p => p.last_ordered,        type: 'text'   },
  is_active:     { get: p => p.is_active,           type: 'number' },
};

const isBlank = (v) => v == null || v === '';

// Rows with no value sort last in both directions, so the products with no
// supplier price stay out of the way instead of filling the top of the table.
const compareBy = (col, dir, pct) => (a, b) => {
  const { get, type } = SORT_COLUMNS[col];
  const av = get(a, pct), bv = get(b, pct);
  if (isBlank(av) && isBlank(bv)) return 0;
  if (isBlank(av)) return 1;
  if (isBlank(bv)) return -1;
  const cmp = type === 'number'
    ? Number(av) - Number(bv)
    // numeric: true so FW_A_RR_2 comes before FW_A_RR_10.
    : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
  return dir === 'asc' ? cmp : -cmp;
};

// Defined outside the page so React does not treat it as a new component type on
// every re-render.
function SortHeader({ col, label, align, sortCol, sortDir, onSort }) {
  const active = sortCol === col;
  const alignClass = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th
      onClick={() => onSort(col)}
      title={`Sort by ${label}`}
      className={`px-2 py-1.5 text-[8px] uppercase font-bold border-b bg-gray-50 cursor-pointer select-none whitespace-nowrap ${alignClass} ${active ? 'text-navy' : 'text-gray-500 hover:text-gray-700'}`}
    >
      {label} {active ? (sortDir === 'asc' ? '▲' : '▼') : <span className="text-gray-300">↕</span>}
    </th>
  );
}

export default function ProductCatalogPage({ initialProductId }) {
  const { toast } = useToast();
  const { currentUser } = useContext(AppContext);
  const canEdit = !!currentUser;

  const [products, setProducts]         = useState([]);
  const [categories, setCategories]     = useState([]);
  const [allManufacturers, setAllManufacturers] = useState([]); // full list for dropdowns, independent of the current filter
  const [filter, setFilter]             = useState('');
  const [search, setSearch]             = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing]           = useState(null);
  const [showCreate, setShowCreate]     = useState(false);
  const [saving, setSaving]             = useState(false);
  const [detail, setDetail]             = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showCatManager, setShowCatManager] = useState(false);
  const [suppliers, setSuppliers]           = useState([]);
  const [addingSupplier, setAddingSupplier] = useState(false);
  const [newSupplier, setNewSupplier]       = useState({ supplier_name: '', supplier_sku: '', unit_price: '', lead_time_days: '', is_preferred: false, notes: '', auto_sync: false, how_to_order: '' });
  const [savingSupplier, setSavingSupplier] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [syncingSupplier, setSyncingSupplier] = useState(null);
  const [tdsynnexSearch, setTdsynnexSearch] = useState('');
  const [tdsynnexResults, setTdsynnexResults] = useState([]);
  const [tdsynnexSearching, setTdsynnexSearching] = useState(false);
  const [tdsynnexSearchDone, setTdsynnexSearchDone] = useState(false);
  const [tdsynnexError, setTdsynnexError] = useState('');
  const [showTdsynnexSearch, setShowTdsynnexSearch] = useState(false);
  const [allSupplierNames, setAllSupplierNames] = useState([]);
  const [tdsynnexUpdateCandidate, setTdsynnexUpdateCandidate] = useState(null);
  const [tdsynnexUpdateToggles, setTdsynnexUpdateToggles] = useState({});
  const [midwichSearch, setMidwichSearch] = useState('');
  const [midwichResults, setMidwichResults] = useState([]);
  const [midwichSearching, setMidwichSearching] = useState(false);
  const [midwichSearchDone, setMidwichSearchDone] = useState(false);
  const [showMidwichSearch, setShowMidwichSearch] = useState(false);
  const [showOneMediaSearch, setShowOneMediaSearch] = useState(false);
  const [midwichPricesAsAt, setMidwichPricesAsAt] = useState(null);
  const [midwichUpdateCandidate, setMidwichUpdateCandidate] = useState(null);
  const [midwichUpdateToggles, setMidwichUpdateToggles] = useState({});
  const [defaultCommPct, setDefaultCommPct]   = useState(15);
  const [replaceSearch,  setReplaceSearch]    = useState('');
  const [replaceResults, setReplaceResults]   = useState([]);
  const [replaceLoading, setReplaceLoading]   = useState(false);
  const [filterMfr, setFilterMfr]           = useState('');
  const [filterTag, setFilterTag]           = useState('');
  // null = leave the server's order (category, then code) alone, so the table
  // looks exactly as it did until a header is actually clicked.
  const [sortCol, setSortCol]               = useState(null);
  const [sortDir, setSortDir]               = useState('asc');
  const isAdmin = parseRoles(currentUser?.user_role).includes('admin');

  // Related products
  const [related, setRelated]               = useState([]);
  const [relatedSearch, setRelatedSearch]   = useState('');
  const [relatedResults, setRelatedResults] = useState([]);
  const [relatedSearching, setRelatedSearching] = useState(false);

  // Bundles manager
  const [showBundles, setShowBundles]       = useState(false);
  const [showMatching, setShowMatching]     = useState(false);

  useEffect(() => {
    api.get('app-settings/product_catalog_default_commission_pct').then(r => {
      if (r?.value) setDefaultCommPct(parseFloat(r.value) || 15);
    }).catch(() => {});
    api.get('product-catalog/supplier-names').then(r => setAllSupplierNames(r || [])).catch(() => {});
  }, []);

  const loadSuppliers = (productId) => {
    if (!productId) { setSuppliers([]); return; }
    api.get(`product-catalog/${productId}/suppliers`).then(setSuppliers).catch(() => setSuppliers([]));
  };

  const loadRelated = (productId) => {
    if (!productId) { setRelated([]); return; }
    api.get(`product-catalog/${productId}/related`).then(r => setRelated(r || [])).catch(() => setRelated([]));
  };

  const searchRelated = async (q) => {
    setRelatedSearch(q);
    if (!q.trim()) { setRelatedResults([]); return; }
    setRelatedSearching(true);
    try {
      const r = await api.get('product-catalog', { search: q, include_inactive: '0' });
      setRelatedResults((r || []).filter(p => p.id !== editing?.id && !related.some(rel => rel.id === p.id)));
    } catch { setRelatedResults([]); }
    setRelatedSearching(false);
  };

  const addRelated = async (relProduct) => {
    await api.post(`product-catalog/${editing.id}/related`, { related_product_id: relProduct.id });
    setRelatedSearch(''); setRelatedResults([]);
    loadRelated(editing.id);
  };

  const removeRelated = async (relId) => {
    await api.delete(`product-catalog/${editing.id}/related/${relId}`);
    loadRelated(editing.id);
  };

  // Adds a supplier-pricing row. Normally reads the inline add form (newSupplier),
  // but an explicit `override` object lets other flows add one directly — e.g.
  // "Update product" from a TD SYNNEX result auto-adds the matching price row.
  const addSupplier = async (override) => {
    const src = override || newSupplier;
    if (!src.supplier_name?.trim() || !editing?.id) return false;
    // Duplicate check: same supplier name + same SKU (case-insensitive name)
    const dupName = src.supplier_name.trim().toLowerCase();
    const dupSku  = (src.supplier_sku || '').trim() || null;
    const isDuplicate = suppliers.some(s =>
      s.supplier_name.toLowerCase() === dupName &&
      (s.supplier_sku || null) === dupSku
    );
    if (isDuplicate) {
      toast.error(`${src.supplier_name.trim()}${dupSku ? ` (${dupSku})` : ''} is already in the supplier list`);
      return false;
    }
    setSavingSupplier(true);
    try {
      await api.post(`product-catalog/${editing.id}/suppliers`, {
        supplier_name: src.supplier_name.trim(),
        supplier_sku: (src.supplier_sku || '').trim() || null,
        unit_price: parseFloat(src.unit_price) || 0,
        lead_time_days: parseInt(src.lead_time_days) || 0,
        is_preferred: src.is_preferred,
        notes: (src.notes || '').trim() || null,
        auto_sync: src.auto_sync,
        how_to_order: (src.how_to_order || '').trim() || null,
      });
      // Only reset the inline form when the add came from it (not an override).
      if (!override) {
        setNewSupplier({ supplier_name: '', supplier_sku: '', unit_price: '', lead_time_days: '', is_preferred: false, notes: '', auto_sync: false, how_to_order: '' });
        setAddingSupplier(false);
      }
      loadSuppliers(editing.id);
      return true;
    } catch (e) { toast.error('Failed: ' + e.message); return false; }
    finally { setSavingSupplier(false); }
  };

  const saveSupplier = async (supplierId, patch) => {
    if (!editing) return;
    await api.put(`product-catalog/${editing.id}/suppliers/${supplierId}`, patch);
    setEditingSupplier(null);
    loadSuppliers(editing.id);
  };

  const deleteSupplier = async (supplierId) => {
    if (!editing || !confirm('Remove this supplier?')) return;
    await api.delete(`product-catalog/${editing.id}/suppliers/${supplierId}`);
    loadSuppliers(editing.id);
  };

  const setPreferred = async (supplierId) => {
    if (!editing) return;
    await api.put(`product-catalog/${editing.id}/suppliers/${supplierId}`, { is_preferred: 1 });
    loadSuppliers(editing.id);
  };

  const syncTdsynnexSupplier = async (supplierId) => {
    setSyncingSupplier(supplierId);
    try {
      const res = await api.post(`product-catalog/${editing.id}/suppliers/${supplierId}/sync`);
      if (res.changed) toast.success(`Price updated: £${Number(res.old_price).toFixed(2)} -> £${Number(res.new_price).toFixed(2)}`);
      else toast.info('Price unchanged');
      // Refresh suppliers
      const data = await api.get(`product-catalog/${editing.id}/suppliers`);
      setSuppliers(data || []);
    } catch (e) {
      toast.error(e?.data?.error || 'Sync failed');
    } finally {
      setSyncingSupplier(null);
    }
  };

  const searchTdsynnex = async () => {
    const term = tdsynnexSearch.trim();
    if (!term) return;
    setTdsynnexSearching(true);
    setTdsynnexResults([]);
    setTdsynnexSearchDone(false);
    setTdsynnexError('');
    try {
      // Route to the correct catalogue search field based on what the user typed:
      // 12-13 digits → EAN/UPC barcode; pure numeric (shorter) → TD SYNNEX part#; else → manufacturer part#
      let searchParams;
      if (/^\d{12,13}$/.test(term)) {
        searchParams = { upcEan: [term] };
      } else if (/^\d+$/.test(term)) {
        searchParams = { tdsynnexPartNumber: [term] };
      } else {
        searchParams = { manufPartNumber: [term] };
      }
      const res = await api.post('tdsynnex/lookup', { page: 1, pageSize: 10, ...searchParams });
      setTdsynnexResults(res.products || res.data || res.items || []);
    } catch (e) {
      // At the search box, not a toast: the person acting on it is right here,
      // and TD SYNNEX's refusal message says how to fix the term.
      setTdsynnexError(e?.data?.error || 'TD SYNNEX search failed');
    } finally {
      setTdsynnexSearching(false);
      setTdsynnexSearchDone(true);
    }
  };

  // Midwich has no live per-item API, so unlike the TD SYNNEX lookup this
  // searches the mirror of their daily price file that the import retains. One
  // box rather than TD SYNNEX's typed routing: the endpoint decides whether the
  // term is a part number, a Midwich SKU, a barcode or free text, and ranks
  // exact identifier matches first.
  const searchMidwich = async () => {
    const term = midwichSearch.trim();
    if (!term) return;
    setMidwichSearching(true);
    setMidwichResults([]);
    setMidwichSearchDone(false);
    try {
      const res = await api.get('midwich/products', { q: term });
      setMidwichResults(res.products || []);
      setMidwichPricesAsAt(res.prices_as_at || null);
    } catch (e) {
      toast.error(e?.data?.error || 'Midwich search failed');
    } finally {
      setMidwichSearching(false);
      setMidwichSearchDone(true);
    }
  };

  // Only one lookup panel open at a time — they sit in the same slot and all
  // write to the same add-supplier form.
  const toggleTdsynnexSearch = () => {
    setShowTdsynnexSearch(s => !s);
    setShowMidwichSearch(false);
    setShowOneMediaSearch(false);
  };
  const toggleMidwichSearch = () => {
    setShowMidwichSearch(s => !s);
    setShowTdsynnexSearch(false);
    setShowOneMediaSearch(false);
  };
  const toggleOneMediaSearch = () => {
    setShowOneMediaSearch(s => !s);
    setShowTdsynnexSearch(false);
    setShowMidwichSearch(false);
  };

  const load = async () => {
    const p = { last_ordered: '1' };
    if (search) p.search = search;
    if (filter) p.category = filter;
    if (showInactive) p.include_inactive = '1';
    const data = await api.get('product-catalog', p);
    setProducts(data);
    loadCategories();
  };
  const loadCategories = () => api.get('product-catalog/categories').then(setCategories).catch(() => {});
  useEffect(() => { load(); }, [filter, search, showInactive]);
  // Full manufacturer list for the add/edit dropdowns — fetched once, not tied to
  // the list's search/category filter (deriving it from the filtered `products`
  // left the dropdown blank whenever a search was active).
  useEffect(() => { api.get('product-catalog/manufacturers').then(m => setAllManufacturers(Array.isArray(m) ? m : [])).catch(() => {}); }, []);

  const initialHandled = useRef(false);
  useEffect(() => {
    if (!initialProductId || initialHandled.current) return;
    if (products.length === 0) return;
    initialHandled.current = true;
    const found = products.find(p => p.id === initialProductId);
    if (found) { openDetail(found); return; }
    api.get(`product-catalog/${initialProductId}`).then(p => { if (p?.id) openDetail(p); }).catch(() => {});
  }, [initialProductId, products]);

  const openDetail = async (p) => {
    setLoadingDetail(true);
    setDetail({ product: p, stats: null, suppliers: [] });
    try {
      const full = await api.get(`product-catalog/${p.id}`);
      setDetail({ product: full, stats: full.stats, suppliers: full.suppliers || [] });
    } catch { setDetail({ product: p, stats: null, suppliers: [] }); }
    setLoadingDetail(false);
  };

  const openEdit = (p) => {
    setEditing({ ...p, tags: parseTags(p) });
    setAddingSupplier(false);
    setEditingSupplier(null);
    setNewSupplier({ supplier_name: '', supplier_sku: '', unit_price: '', lead_time_days: '', is_preferred: false, notes: '', auto_sync: false, how_to_order: '' });
    setRelatedSearch(''); setRelatedResults([]);
    loadSuppliers(p.id);
    loadRelated(p.id);
  };

  const save = async () => {
    // If the add-supplier row is open with data filled in, flush it first
    if (addingSupplier && newSupplier.supplier_name.trim()) {
      const ok = await addSupplier();
      if (!ok) return; // duplicate or error — stop
    } else if (addingSupplier) {
      // Row open but empty — just dismiss it
      setAddingSupplier(false);
    }
    setSaving(true);
    try {
      const patch = {
        description: editing.description,
        category: editing.category,
        manufacturer: editing.manufacturer,
        manufacturer_part_number: editing.manufacturer_part_number || null,
        sell_price: editing.sell_price,
        commission_pct: editing.commission_pct,
        notes: editing.notes,
        bulk_tracked: editing.bulk_tracked ? 1 : 0,
        tags: Array.isArray(editing.tags) ? editing.tags : parseTags(editing),
      };
      if (editing._replaced_by_id !== undefined) {
        patch.replaced_by_id = editing._replaced_by_id || null;
        patch.replaced_at    = editing._replaced_by_id ? new Date().toISOString().slice(0, 10) : null;
        if (editing._replaced_by_id) patch.is_active = 0;
      }
      await api.put(`product-catalog/${editing.id}`, patch);
      setEditing(null);
      setReplaceSearch(''); setReplaceResults([]);
      load();
    } catch (e) { toast.error('Save failed: ' + e.message); }
    setSaving(false);
  };

  const searchReplacements = async (q) => {
    setReplaceSearch(q);
    if (!q.trim()) { setReplaceResults([]); return; }
    setReplaceLoading(true);
    try {
      const r = await api.get('product-catalog', { search: q, include_inactive: '1' });
      setReplaceResults((r || []).filter(p => p.id !== editing?.id));
    } catch { setReplaceResults([]); }
    setReplaceLoading(false);
  };

  const create = async (form) => {
    setSaving(true);
    try {
      const created = await api.post('product-catalog', form);
      setShowCreate(false);
      load();
      if (created?.id) openEdit(created);
    } catch (e) { toast.error('Failed: ' + e.message); }
    setSaving(false);
  };

  const toggle = async (p) => {
    await api.put(`product-catalog/${p.id}`, { is_active: p.is_active ? 0 : 1 });
    load();
  };

  const exportCsv = () => {
    const cols = ['code','description','category','manufacturer','tags','sell_price','commission_pct','notes','is_active'];
    const rows = filtered.map(p =>
      cols.map(c => (c === 'tags' ? parseTags(p).join('; ') : (p[c] ?? '')))
    );
    const csv = toCsv(cols, rows);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'product-catalog.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const renameCategory = async (oldName, newName) => {
    const inCat = products.filter(p => p.category === oldName);
    await Promise.all(inCat.map(p => api.put(`product-catalog/${p.id}`, { category: newName })));
    if (filter === oldName) setFilter(newName);
    load();
  };

  const manufacturers = [...new Set(products.filter(p => p.manufacturer).map(p => p.manufacturer))].sort();
  // Manufacturer chips only appear once a category has narrowed the list; across
  // the whole catalogue there are too many to be a useful filter.
  const showMfrFilter = !!filter && manufacturers.length > 0;
  // A manufacturer picked under one category rarely exists in the next, and
  // with no category the row is hidden, so changing category clears it.
  const pickCategory = (cat) => { setFilter(cat); setFilterMfr(''); };
  // Options for the add/edit dropdowns: the full catalog list plus anything in the
  // current view (covers a just-added manufacturer before the full list refetches).
  const mfrOptions = [...new Set([...allManufacturers, ...manufacturers])].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  // All tags in use across the catalog, with counts — powers the tag filter,
  // the overview strip, and the create/edit suggestions.
  const allTags = [...new Set(products.flatMap(p => parseTags(p)))].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  const tagCount = (t) => products.filter(p => parseTags(p).some(x => x.toLowerCase() === t.toLowerCase())).length;
  const matching = products.filter(p =>
    (!filter    || p.category     === filter) &&
    (!filterMfr || p.manufacturer === filterMfr) &&
    (!filterTag || parseTags(p).some(t => t.toLowerCase() === filterTag.toLowerCase()))
  );
  // The sort lands on `filtered` itself, so Export CSV writes the rows in the
  // order shown on screen. The list endpoint has no LIMIT, so this sorts the
  // whole result set rather than a page of it.
  const filtered = sortCol ? [...matching].sort(compareBy(sortCol, sortDir, defaultCommPct)) : matching;

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };
  const sortProps = { sortCol, sortDir, onSort: toggleSort };

  // The edit form's SOW Price box stays empty for an automatic price, so the
  // box shows what blank will sell at and the strip below names which it is.
  const commPct = commissionPctOf(editing, defaultCommPct);
  const suggestedSell = autoSellPrice(editing, defaultCommPct);
  const editSell = editing ? sowPrice(editing, defaultCommPct) : { price: null, auto: false };

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-extrabold text-navy">Product Catalog</h2>
          <div className="text-[9px] text-gray-500">{filtered.length} products · {categories.length} categories</div>
        </div>
        <div className="flex gap-2">
          {canEdit && <button onClick={exportCsv} className="px-3 py-1.5 border border-gray-300 text-gray-600 text-[10px] font-semibold rounded hover:bg-gray-50">↓ Export CSV</button>}
          {canEdit && <button onClick={() => setShowCatManager(true)} className="px-3 py-1.5 border border-gray-300 text-gray-600 text-[10px] font-semibold rounded hover:bg-gray-50">⊞ Categories</button>}
          {canEdit && <button onClick={() => setShowBundles(true)} className="px-3 py-1.5 border border-gray-300 text-gray-600 text-[10px] font-semibold rounded hover:bg-gray-50">⊛ Bundles</button>}
          {canEdit && <button onClick={() => setShowMatching(true)} className="px-3 py-1.5 border border-gray-300 text-gray-600 text-[10px] font-semibold rounded hover:bg-gray-50">⇄ Supplier Matching</button>}
          {canEdit && <button onClick={() => setShowCreate(true)} className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80">+ Add Product</button>}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <input className="form-input max-w-[200px]" placeholder="Search code, part no. or description…" value={search} onChange={e => setSearch(e.target.value)} />
        <label className="flex items-center gap-1 text-[9px] text-gray-500 cursor-pointer">
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} className="w-3 h-3" />
          Show inactive
        </label>
      </div>

      <div className={`flex flex-wrap gap-1 ${showMfrFilter || allTags.length > 0 ? 'mb-1' : 'mb-3'}`}>
        <span className="text-[7.5px] text-gray-400 font-semibold uppercase self-center mr-1">Category</span>
        <button onClick={() => pickCategory('')} className={`px-2 py-0.5 rounded text-[8px] font-semibold ${!filter ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>All</button>
        {categories.map(cat => (
          <button key={cat} onClick={() => pickCategory(filter === cat ? '' : cat)} className={`px-2 py-0.5 rounded text-[8px] font-semibold ${filter === cat ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {cat} ({products.filter(p => p.category === cat).length})
          </button>
        ))}
      </div>
      {showMfrFilter && (
        <div className="flex flex-wrap gap-1 mb-3">
          <span className="text-[7.5px] text-gray-400 font-semibold uppercase self-center mr-1">Manufacturer</span>
          <button onClick={() => setFilterMfr('')} className={`px-2 py-0.5 rounded text-[8px] font-semibold ${!filterMfr ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>All</button>
          {manufacturers.map(mfr => (
            <button key={mfr} onClick={() => setFilterMfr(filterMfr === mfr ? '' : mfr)} className={`px-2 py-0.5 rounded text-[8px] font-semibold ${filterMfr === mfr ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {mfr} ({products.filter(p => p.manufacturer === mfr).length})
            </button>
          ))}
        </div>
      )}
      {allTags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          <span className="text-[7.5px] text-gray-400 font-semibold uppercase self-center mr-1">Tags</span>
          <button onClick={() => setFilterTag('')} className={`px-2 py-0.5 rounded text-[8px] font-semibold ${!filterTag ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>All</button>
          {allTags.map(t => (
            <button key={t} onClick={() => setFilterTag(filterTag === t ? '' : t)} className={`px-2 py-0.5 rounded text-[8px] font-semibold ${filterTag === t ? 'bg-navy text-white' : 'bg-navy/10 text-navy hover:bg-navy/20'}`}>
              {t} ({tagCount(t)})
            </button>
          ))}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <SortHeader col="code"          label="Code"          {...sortProps} />
              <SortHeader col="description"   label="Product"       {...sortProps} />
              <SortHeader col="manufacturer"  label="Manufacturer"  {...sortProps} />
              <SortHeader col="category"      label="Category"      {...sortProps} />
              <SortHeader col="min_price"     label="Price Range £" align="right"  {...sortProps} />
              <SortHeader col="sell_price"    label="SOW Price £"   align="right"  {...sortProps} />
              <SortHeader col="commission"    label="Commission"    align="right"  {...sortProps} />
              <SortHeader col="price_updated" label="Price Updated" {...sortProps} />
              <SortHeader col="last_ordered"  label="Last Ordered"  {...sortProps} />
              <SortHeader col="is_active"     label="Active"        align="center" {...sortProps} />
              <th className="px-2 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => (
              <tr key={p.id} onClick={() => openDetail(p)} className={`border-b border-gray-100 hover:bg-gold/5 cursor-pointer ${!p.is_active ? 'opacity-40' : ''}`}>
                <td className="px-2 py-1.5 text-[9px] font-mono font-semibold text-navy">{p.code}</td>
                <td className="px-2 py-1.5 max-w-[260px]">
                  <div className="text-[11px] font-bold text-navy truncate">{p.description}</div>
                  {p.manufacturer_part_number && <div className="text-[8px] font-mono text-gray-500 truncate leading-tight">{p.manufacturer_part_number}</div>}
                  {p.replaced_by_id && <div className="text-[7px] text-orange-600 font-semibold mt-0.5">↩ Replaced by {p.replaced_by_code || p.replaced_by_name}</div>}
                  {p.notes && !p.replaced_by_id && <div className="text-[8px] text-gray-400 truncate">{p.notes}</div>}
                  {parseTags(p).length > 0 && (
                    <div className="flex flex-wrap gap-0.5 mt-0.5">
                      {parseTags(p).map(t => <span key={t} className="text-[7px] bg-navy/10 text-navy px-1 py-0.5 rounded font-semibold">{t}</span>)}
                    </div>
                  )}
                </td>
                <td className="px-2 py-1.5 text-[9px] text-gray-600">{p.manufacturer || '—'}</td>
                <td className="px-2 py-1.5"><span className="text-[8px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-medium">{p.category}</span></td>
                <td className="px-2 py-1.5 text-[10px] font-semibold text-right text-gray-700">
                  {fmtRange(p.min_price, p.max_price)}
                  {p.supplier_count > 0 && <div className="text-[7px] text-gray-400">{p.supplier_count} supplier{p.supplier_count !== 1 ? 's' : ''}</div>}
                </td>
                <td className="px-2 py-1.5 text-[10px] font-semibold text-right text-blue-600">
                  {fmt(sowPrice(p, defaultCommPct).price)}
                  {sowPrice(p, defaultCommPct).auto && <div className="text-[7px] text-gray-400 font-normal" title="No SOW price set: cheapest supplier price plus commission">auto · {commissionPctOf(p, defaultCommPct)}%</div>}
                </td>
                <td className="px-2 py-1.5 text-[10px] font-semibold text-right">{fmtComm(p.min_price, sowPrice(p, defaultCommPct).price)}</td>
                <td className="px-2 py-1.5 text-[9px] text-gray-500">{p.price_last_modified ? formatDate(p.price_last_modified) : '—'}</td>
                <td className="px-2 py-1.5 text-[9px] text-gray-500">{p.last_ordered ? formatDate(p.last_ordered) : '—'}</td>
                <td className="px-2 py-1.5 text-center" onClick={e => e.stopPropagation()}>
                  {canEdit ? (
                    <button onClick={() => toggle(p)} className={`w-8 h-4 rounded-full transition-colors ${p.is_active ? 'bg-emerald-500' : 'bg-gray-300'}`}>
                      <div className={`w-3 h-3 rounded-full bg-white shadow mx-0.5 transition-transform ${p.is_active ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                  ) : (
                    <span className={`inline-block w-2 h-2 rounded-full ${p.is_active ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                  )}
                </td>
                <td className="px-2 py-1.5 text-right" onClick={e => e.stopPropagation()}>
                  {canEdit && <button onClick={() => openEdit(p)} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[9px] font-semibold rounded hover:bg-gray-200">Edit</button>}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={11} className="text-center py-8 text-[10px] text-gray-400">No products found</td></tr>}
          </tbody>
        </table>
      </div>

      {detail && (
        <ProductDetailModal
          product={detail.product}
          stats={detail.stats}
          loading={loadingDetail}
          canEdit={canEdit}
          defaultCommPct={defaultCommPct}
          onEdit={() => { openEdit(detail.product); setDetail(null); }}
          onClose={() => setDetail(null)}
        />
      )}

      {editing && (
        <Modal title={`Edit: ${editing.code}`} wide onClose={() => setEditing(null)} footer={
          <>
            <button onClick={() => setEditing(null)} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
            <button onClick={save} disabled={saving} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded">{saving ? 'Saving...' : 'Save'}</button>
          </>
        }>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Description</label>
              <input className="form-input" value={editing.description || ''} onChange={e => setEditing(p => ({ ...p, description: e.target.value }))} />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Manufacturer</label>
              <ManufacturerSelect manufacturers={mfrOptions} value={editing.manufacturer || ''} onChange={v => setEditing(p => ({ ...p, manufacturer: v }))} />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Category</label>
              <CategorySelect categories={categories} value={editing.category || ''} onChange={v => setEditing(p => ({ ...p, category: v }))} />
            </div>
            <div className="col-span-2">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Manufacturer Part Number <span className="text-gray-400 font-normal">(e.g. DELL-MDS19)</span></label>
              <input className="form-input font-mono" value={editing.manufacturer_part_number || ''} onChange={e => setEditing(p => ({ ...p, manufacturer_part_number: e.target.value }))} placeholder="Manufacturer model / part number..." />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">
                SOW Price (£) <span className="text-gray-400 font-normal">(blank = supplier price + commission)</span>
              </label>
              <input className="form-input" type="number" step="0.01" value={editing.sell_price ?? ''} onChange={e => setEditing(p => ({ ...p, sell_price: e.target.value ? parseFloat(e.target.value) : null }))} placeholder={suggestedSell != null ? `Auto: ${fmt(suggestedSell)}` : 'Auto once a supplier is priced'} />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">
                Commission Override % <span className="text-gray-400 font-normal">(blank = use default {defaultCommPct}%)</span>
              </label>
              <input className="form-input" type="number" step="0.1" min="0" value={editing.commission_pct ?? ''} onChange={e => setEditing(p => ({ ...p, commission_pct: e.target.value ? parseFloat(e.target.value) : null }))} placeholder={`${defaultCommPct}`} />
            </div>

            {(editing.min_price != null || editing.sell_price != null) && (
              <div className="col-span-2 p-2 bg-gray-50 rounded text-[9px] grid grid-cols-3 gap-2">
                <div>
                  <div className="text-[7.5px] text-gray-400 uppercase font-bold mb-0.5">Supplier Price Range</div>
                  <div className="font-semibold">{fmtRange(editing.min_price, editing.max_price)}</div>
                </div>
                <div>
                  <div className="text-[7.5px] text-gray-400 uppercase font-bold mb-0.5">{editSell.auto ? 'SOW Price (auto)' : 'Suggested Sell'} @ {commPct}%</div>
                  <div className="font-semibold text-blue-600">{suggestedSell != null ? fmt(suggestedSell) : '—'}</div>
                </div>
                {editSell.price != null && editing.min_price != null && (
                  <div>
                    <div className="text-[7.5px] text-gray-400 uppercase font-bold mb-0.5">Commission</div>
                    <div className={`font-bold ${editSell.price - Number(editing.min_price) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      £{(editSell.price - Number(editing.min_price)).toFixed(2)}
                      {editing.min_price > 0 && <span className="text-gray-400 font-normal ml-1">({((editSell.price - Number(editing.min_price)) / Number(editing.min_price) * 100).toFixed(1)}%)</span>}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="col-span-2">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Notes</label>
              <input className="form-input" value={editing.notes || ''} onChange={e => setEditing(p => ({ ...p, notes: e.target.value }))} />
            </div>
            <div className="col-span-2">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Tags <span className="text-gray-400 font-normal">(e.g. ECS, SMS, Audio Visual)</span></label>
              <TagEditor tags={Array.isArray(editing.tags) ? editing.tags : parseTags(editing)} suggestions={allTags} onChange={v => setEditing(p => ({ ...p, tags: v }))} />
            </div>
            <div className="col-span-2 flex items-start gap-2 p-2 bg-gray-50 border border-gray-200 rounded">
              <input type="checkbox" id="bulk-tracked" className="mt-0.5" checked={!!editing.bulk_tracked}
                onChange={e => setEditing(p => ({ ...p, bulk_tracked: e.target.checked ? 1 : 0 }))} />
              <label htmlFor="bulk-tracked" className="text-[9px] text-gray-600 cursor-pointer">
                <span className="font-bold text-gray-700">Bulk / quantity tracked</span> — keep a running count per location instead of individual asset-tagged items.
                Use for low-value consumables (e.g. patch cables). Default off (each unit tracked individually).
              </label>
            </div>
            <div className="col-span-2">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Code <span className="text-gray-400 font-normal">(auto-generated)</span></label>
              <input className="form-input font-mono bg-gray-50 text-gray-400" value={editing.code || ''} readOnly />
            </div>

            <div className="col-span-2 border-t border-gray-100 pt-3 mt-1">
              <div className="text-[8px] font-bold text-gray-500 uppercase mb-1">Consolidation</div>
              <div className="text-[8px] text-gray-400 mb-2">Mark this product as replaced by another — it will be deactivated and the replacement shown in the catalog.</div>
              {(editing._replaced_by_id || editing.replaced_by_id) ? (
                <div className="flex items-center gap-2 p-2 bg-orange-50 border border-orange-200 rounded">
                  <div className="flex-1">
                    <div className="text-[8px] text-orange-700 font-semibold">Replaced by:</div>
                    <div className="text-[9px] font-bold text-gray-800">
                      {editing._replaced_by_code || editing.replaced_by_code} — {editing._replaced_by_desc || editing.replaced_by_name}
                    </div>
                  </div>
                  <button onClick={() => setEditing(p => ({ ...p, _replaced_by_id: null, _replaced_by_code: null, _replaced_by_desc: null }))}
                    className="text-[8px] text-red-500 hover:text-red-700 underline whitespace-nowrap">Clear replacement</button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    className="form-input text-[10px] pr-8"
                    placeholder="Search for replacement product by code or name…"
                    value={replaceSearch}
                    onChange={e => searchReplacements(e.target.value)}
                  />
                  {replaceLoading && <span className="absolute right-2 top-2 text-[9px] text-gray-400">…</span>}
                  {replaceResults.length > 0 && (
                    <div className="absolute z-20 left-0 right-0 top-full mt-0.5 bg-white border border-gray-200 rounded shadow-lg max-h-40 overflow-y-auto">
                      {replaceResults.map(r => (
                        <div key={r.id}
                          className="px-2 py-1.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                          onClick={() => {
                            setEditing(p => ({ ...p, _replaced_by_id: r.id, _replaced_by_code: r.code, _replaced_by_desc: r.description }));
                            setReplaceSearch(''); setReplaceResults([]);
                          }}>
                          <div className="text-[9px] font-mono font-semibold text-navy">{r.code}</div>
                          <div className="text-[8px] text-gray-600 truncate">{r.description}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="col-span-2 border-t border-gray-100 pt-3 mt-1">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[8px] font-bold text-gray-500 uppercase">Related Products</div>
                <span className="text-[7px] text-gray-400">Suggested when ordering this product</span>
              </div>
              <div className="space-y-1 mb-2">
                {related.map(r => (
                  <div key={r.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded px-2 py-1">
                    <div>
                      <span className="text-[8px] font-mono font-bold text-navy mr-2">{r.code}</span>
                      <span className="text-[9px]">{r.description}</span>
                      {r.manufacturer && <span className="text-[8px] text-gray-400 ml-2">{r.manufacturer}</span>}
                    </div>
                    <button onClick={() => removeRelated(r.id)} className="text-red-400 hover:text-red-600 text-[11px] font-bold ml-3 flex-shrink-0">×</button>
                  </div>
                ))}
                {related.length === 0 && <div className="text-[8px] text-gray-400">No related products yet</div>}
              </div>
              <div className="relative">
                <input
                  className="form-input text-[9px]"
                  placeholder="Search to add a related product…"
                  value={relatedSearch}
                  onChange={e => searchRelated(e.target.value)}
                />
                {relatedSearching && <span className="absolute right-2 top-2 text-[9px] text-gray-400">…</span>}
                {relatedResults.length > 0 && (
                  <div className="absolute z-20 left-0 right-0 top-full mt-0.5 bg-white border border-gray-200 rounded shadow-lg max-h-40 overflow-y-auto">
                    {relatedResults.map(r => (
                      <div key={r.id}
                        className="px-2 py-1.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                        onClick={() => addRelated(r)}>
                        <span className="text-[8px] font-mono font-bold text-navy mr-2">{r.code}</span>
                        <span className="text-[9px]">{r.description}</span>
                        {r.manufacturer && <span className="text-[8px] text-gray-400 ml-2">{r.manufacturer}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="col-span-2 border-t border-gray-100 pt-3 mt-1">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-[8px] font-bold text-gray-500 uppercase">Supplier Pricing</div>
                  <div className="text-[7px] text-gray-400">Prices drive the cost range. Preferred supplier is the recommended source.</div>
                </div>
                <div className="flex gap-2 items-center">
                  <button
                    type="button"
                    className="btn-secondary text-[9px] px-2 py-1"
                    onClick={toggleTdsynnexSearch}
                  >
                    TD SYNNEX Lookup
                  </button>
                  <button
                    type="button"
                    className="btn-secondary text-[9px] px-2 py-1"
                    onClick={toggleMidwichSearch}
                  >
                    Midwich Lookup
                  </button>
                  <button
                    type="button"
                    className="btn-secondary text-[9px] px-2 py-1"
                    onClick={toggleOneMediaSearch}
                  >
                    OneMedia Lookup
                  </button>
                  <button onClick={() => setAddingSupplier(true)} className="px-2 py-0.5 bg-navy text-white text-[9px] font-semibold rounded hover:bg-navy/80">+ Add</button>
                </div>
              </div>
              {showTdsynnexSearch && (
                <div className="border border-blue-200 rounded p-2 bg-blue-50 space-y-2 mb-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="form-input flex-1 text-[9px]"
                      placeholder="Part number (P2425H or DELL-P2425H), TD SYNNEX# or EAN barcode..."
                      value={tdsynnexSearch}
                      onChange={e => setTdsynnexSearch(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && searchTdsynnex()}
                    />
                    <button className="btn-primary text-[9px] px-2 py-1" onClick={searchTdsynnex} disabled={tdsynnexSearching}>
                      {tdsynnexSearching ? 'Searching…' : 'Search'}
                    </button>
                  </div>
                  {tdsynnexSearching && (
                    <div className="flex items-center gap-2 py-2 text-[9px] text-blue-600">
                      <svg className="animate-spin w-3 h-3 text-blue-500" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
                      Searching TD SYNNEX…
                    </div>
                  )}
                  {tdsynnexError && (
                    <div className="text-[9px] text-red-600 py-1">{tdsynnexError}</div>
                  )}
                  {tdsynnexSearchDone && !tdsynnexSearching && !tdsynnexError && tdsynnexResults.length === 0 && (
                    <div className="text-center py-2 text-[9px] text-gray-400">No results found for "{tdsynnexSearch}"</div>
                  )}
                  {tdsynnexResults.length > 0 && (
                    <div className="space-y-1.5">
                      {tdsynnexResults.map((r, i) => {
                        const custPrice = r.price?.customerPrice ?? r.price?.listPrice ?? null;
                        const listPrice = r.price?.listPrice ?? null;
                        const desc = r.productDescription || r.description || r.productName || r.name || '';
                        const mfr = r.manufacturer || r.vendorName || r.manufacturerName || r.brand || '';
                        const statusNote = r.productStatusCode ? TDSYNNEX_STATUS_LABELS[r.productStatusCode] : null;
                        return (
                          <div key={i} className="border border-blue-100 rounded p-2 bg-white">
                            <div className="flex items-start gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-mono font-bold text-[9px] text-navy">{r.manufPartNumber || r.manufacturerPartNumber}</span>
                                  {r.tdsynnexPartNumber && <span className="text-[7px] text-gray-400 font-mono">TD: {r.tdsynnexPartNumber}</span>}
                                  {r.productStatusCode && r.productStatusCode !== 'Active' && (
                                    <span className="text-[7px] px-1 rounded bg-amber-100 text-amber-700 cursor-help" title={statusNote || r.productStatusCode}>{r.productStatusCode}</span>
                                  )}
                                </div>
                                {statusNote && r.productStatusCode !== 'Active' && (
                                  <div className="text-[7px] text-amber-600 mt-0.5">{statusNote}</div>
                                )}
                                {desc && <div className="text-[8px] text-gray-800 mt-0.5 font-medium">{desc}</div>}
                                <div className="text-[7px] text-gray-400 mt-0.5 flex gap-2 flex-wrap">
                                  {mfr && <span>{mfr}</span>}
                                  {r.familyDesc && <span>· {r.familyDesc}</span>}
                                  {r.classDesc && <span>· {r.classDesc}</span>}
                                  {r.subclassDesc && <span>· {r.subclassDesc}</span>}
                                </div>
                              </div>
                              <div className="text-right flex-shrink-0">
                                {custPrice != null && <div className="text-[11px] font-extrabold text-emerald-700">£{Number(custPrice).toFixed(2)}</div>}
                                {listPrice != null && listPrice !== custPrice && <div className="text-[7px] text-gray-400 line-through">£{Number(listPrice).toFixed(2)}</div>}
                                {custPrice == null && <div className="text-[9px] text-gray-400">price n/a</div>}
                                <div className="flex gap-2 justify-end mt-1">
                                  <button
                                    className="text-[8px] font-semibold text-emerald-700 underline"
                                    onClick={() => {
                                      const fields = {
                                        description: { label: 'Description', current: editing.description, proposed: desc },
                                        manufacturer: { label: 'Manufacturer', current: editing.manufacturer, proposed: mfr },
                                        manufacturer_part_number: { label: 'Manuf Part No.', current: editing.manufacturer_part_number, proposed: r.manufPartNumber || r.manufacturerPartNumber || '' },
                                        category: { label: 'Category', current: editing.category, proposed: r.classDesc || r.familyDesc || '' },
                                      };
                                      setTdsynnexUpdateCandidate({ result: r, fields });
                                      setTdsynnexUpdateToggles(Object.fromEntries(
                                        Object.entries(fields)
                                          .map(([k, v]) => [k, !!(v.proposed && v.proposed !== v.current)])
                                      ));
                                    }}
                                  >Update product</button>
                                  <button
                                    className="text-[8px] font-semibold text-blue-600 underline"
                                    onClick={() => {
                                      const manufRef = r.manufPartNumber || r.manufacturerPartNumber || '';
                                      setNewSupplier(s => ({
                                        ...s,
                                        supplier_name: 'TD SYNNEX',
                                        supplier_sku: r.tdsynnexPartNumber || manufRef,
                                        unit_price: custPrice != null ? String(custPrice) : s.unit_price,
                                        auto_sync: true,
                                        notes: [manufRef, desc].filter(Boolean).join(' — ').slice(0, 100),
                                      }));
                                      setShowTdsynnexSearch(false);
                                      setAddingSupplier(true);
                                    }}
                                  >Use</button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {tdsynnexUpdateCandidate && (
                    <div className="border border-emerald-200 bg-emerald-50 rounded p-2 space-y-1.5">
                      <div className="text-[8px] font-bold text-emerald-800">Update product fields from TD SYNNEX?</div>
                      {Object.entries(tdsynnexUpdateCandidate.fields).filter(([, v]) => v.proposed).map(([key, v]) => (
                        <div key={key} className="flex items-start gap-2">
                          <button
                            type="button"
                            className={`mt-0.5 w-7 h-3.5 rounded-full flex-shrink-0 transition-colors ${tdsynnexUpdateToggles[key] ? 'bg-emerald-500' : 'bg-gray-300'}`}
                            onClick={() => setTdsynnexUpdateToggles(t => ({ ...t, [key]: !t[key] }))}
                          >
                            <div className={`w-3 h-3 rounded-full bg-white shadow mx-0.5 transition-transform ${tdsynnexUpdateToggles[key] ? 'translate-x-3' : ''}`} />
                          </button>
                          <div className="flex-1 min-w-0 text-[7.5px]">
                            <div className="font-bold text-gray-500 uppercase">{v.label}</div>
                            <div className="text-gray-400 truncate">
                              <span className="line-through">{v.current || '—'}</span>
                              <span className="mx-1 text-gray-300">→</span>
                              <span className={tdsynnexUpdateToggles[key] ? 'text-emerald-700 font-semibold' : 'text-gray-500'}>{v.proposed}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="flex gap-2 pt-0.5">
                        <button
                          className="text-[8px] px-2 py-0.5 bg-emerald-600 text-white rounded font-semibold"
                          onClick={async () => {
                            const updates = {};
                            if (tdsynnexUpdateToggles.description && tdsynnexUpdateCandidate.fields.description.proposed) updates.description = tdsynnexUpdateCandidate.fields.description.proposed;
                            if (tdsynnexUpdateToggles.manufacturer && tdsynnexUpdateCandidate.fields.manufacturer.proposed) updates.manufacturer = tdsynnexUpdateCandidate.fields.manufacturer.proposed;
                            if (tdsynnexUpdateToggles.manufacturer_part_number && tdsynnexUpdateCandidate.fields.manufacturer_part_number.proposed) updates.manufacturer_part_number = tdsynnexUpdateCandidate.fields.manufacturer_part_number.proposed;
                            if (tdsynnexUpdateToggles.category && tdsynnexUpdateCandidate.fields.category.proposed) updates.category = tdsynnexUpdateCandidate.fields.category.proposed;
                            setEditing(p => ({ ...p, ...updates }));
                            // Also add the TD SYNNEX price as a supplier-pricing row
                            // (auto-"Use") so updating from a result captures both the
                            // product detail and the source price in one click.
                            const r = tdsynnexUpdateCandidate.result || {};
                            const custPrice = r.price?.customerPrice ?? r.price?.listPrice ?? null;
                            const manufRef = r.manufPartNumber || r.manufacturerPartNumber || '';
                            const desc = r.productDescription || r.description || r.productName || r.name || '';
                            const added = await addSupplier({
                              supplier_name: 'TD SYNNEX',
                              supplier_sku: r.tdsynnexPartNumber || manufRef,
                              unit_price: custPrice != null ? String(custPrice) : '',
                              lead_time_days: '',
                              is_preferred: false,
                              notes: [manufRef, desc].filter(Boolean).join(' — ').slice(0, 100),
                              auto_sync: true,
                            });
                            if (added) toast.success('Product updated & TD SYNNEX price added to supplier pricing');
                            setTdsynnexUpdateCandidate(null);
                          }}
                        >Apply selected</button>
                        <button className="text-[8px] px-2 py-0.5 border border-gray-300 rounded text-gray-500" onClick={() => setTdsynnexUpdateCandidate(null)}>Dismiss</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {showMidwichSearch && (
                <div className="border border-amber-200 rounded p-2 bg-amber-50 space-y-2 mb-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="form-input flex-1 text-[9px]"
                      placeholder="Part number, Midwich SKU, barcode or description..."
                      value={midwichSearch}
                      onChange={e => setMidwichSearch(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && searchMidwich()}
                    />
                    <button className="btn-primary text-[9px] px-2 py-1" onClick={searchMidwich} disabled={midwichSearching}>
                      {midwichSearching ? 'Searching…' : 'Search'}
                    </button>
                  </div>
                  {/* Midwich prices are only ever as fresh as the last daily file, so say
                      when that was rather than let them read as live like TD SYNNEX's. */}
                  <div className="text-[7px] text-amber-700">
                    {midwichPricesAsAt
                      ? `From Midwich's price file of ${fmtDateTimeLong(midwichPricesAsAt)}.`
                      : 'From the daily Midwich price file.'}
                  </div>
                  {midwichSearching && (
                    <div className="flex items-center gap-2 py-2 text-[9px] text-amber-700">
                      <svg className="animate-spin w-3 h-3 text-amber-600" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
                      Searching the Midwich price file…
                    </div>
                  )}
                  {midwichSearchDone && !midwichSearching && midwichResults.length === 0 && (
                    <div className="text-center py-2 text-[9px] text-gray-400">Nothing in the latest Midwich file matches "{midwichSearch}"</div>
                  )}
                  {midwichResults.length > 0 && (
                    <div className="space-y-1.5">
                      {midwichResults.map((r) => (
                        <div key={r.midwich_sku} className="border border-amber-100 rounded p-2 bg-white">
                          <div className="flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono font-bold text-[9px] text-navy">{r.manufacturer_part_number || '—'}</span>
                                <span className="text-[7px] text-gray-400 font-mono">MW: {r.midwich_sku}</span>
                                {r.midwich_status && (
                                  <span className="text-[7px] px-1 rounded bg-gray-100 text-gray-600">{r.midwich_status}</span>
                                )}
                              </div>
                              {r.description && <div className="text-[8px] text-gray-800 mt-0.5 font-medium">{r.description}</div>}
                              <div className="text-[7px] text-gray-400 mt-0.5 flex gap-2 flex-wrap">
                                {r.manufacturer && <span>{r.manufacturer}</span>}
                                {r.category && <span>· {r.category}</span>}
                                {r.barcode && <span>· EAN {r.barcode}</span>}
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              {r.cost_price != null
                                ? <div className="text-[11px] font-extrabold text-emerald-700">{fmt(r.cost_price)}</div>
                                : <div className="text-[9px] text-gray-400">price n/a</div>}
                              {r.rrp != null && Number(r.rrp) > 0 && <div className="text-[7px] text-gray-400">RRP {fmt(r.rrp)}</div>}
                              <div className={`text-[7px] mt-0.5 ${Number(r.stock_qty) > 0 ? 'text-gray-500' : 'text-amber-600'}`}>
                                {Number(r.stock_qty) > 0 ? `${r.stock_qty} in stock` : 'no stock'}
                              </div>
                              <div className="flex gap-2 justify-end mt-1">
                                <button
                                  className="text-[8px] font-semibold text-emerald-700 underline"
                                  onClick={() => {
                                    const fields = {
                                      description: { label: 'Description', current: editing.description, proposed: r.description || '' },
                                      manufacturer: { label: 'Manufacturer', current: editing.manufacturer, proposed: r.manufacturer || '' },
                                      manufacturer_part_number: { label: 'Manuf Part No.', current: editing.manufacturer_part_number, proposed: r.manufacturer_part_number || '' },
                                      category: { label: 'Category', current: editing.category, proposed: r.category || '' },
                                    };
                                    setMidwichUpdateCandidate({ result: r, fields });
                                    setMidwichUpdateToggles(Object.fromEntries(
                                      Object.entries(fields)
                                        .map(([k, v]) => [k, !!(v.proposed && v.proposed !== v.current)])
                                    ));
                                  }}
                                >Update product</button>
                                <button
                                  className="text-[8px] font-semibold text-blue-600 underline"
                                  onClick={() => {
                                    setNewSupplier(s => ({
                                      ...s,
                                      supplier_name: 'Midwich',
                                      supplier_sku: r.midwich_sku,
                                      unit_price: r.cost_price != null ? String(r.cost_price) : s.unit_price,
                                      auto_sync: true,
                                      notes: [r.manufacturer_part_number, r.description].filter(Boolean).join(' — ').slice(0, 100),
                                    }));
                                    setShowMidwichSearch(false);
                                    setAddingSupplier(true);
                                  }}
                                >Use</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {midwichUpdateCandidate && (
                    <div className="border border-emerald-200 bg-emerald-50 rounded p-2 space-y-1.5">
                      <div className="text-[8px] font-bold text-emerald-800">Update product fields from Midwich?</div>
                      {Object.entries(midwichUpdateCandidate.fields).filter(([, v]) => v.proposed).map(([key, v]) => (
                        <div key={key} className="flex items-start gap-2">
                          <button
                            type="button"
                            className={`mt-0.5 w-7 h-3.5 rounded-full flex-shrink-0 transition-colors ${midwichUpdateToggles[key] ? 'bg-emerald-500' : 'bg-gray-300'}`}
                            onClick={() => setMidwichUpdateToggles(t => ({ ...t, [key]: !t[key] }))}
                          >
                            <div className={`w-3 h-3 rounded-full bg-white shadow mx-0.5 transition-transform ${midwichUpdateToggles[key] ? 'translate-x-3' : ''}`} />
                          </button>
                          <div className="flex-1 min-w-0 text-[7.5px]">
                            <div className="font-bold text-gray-500 uppercase">{v.label}</div>
                            <div className="text-gray-400 truncate">
                              <span className="line-through">{v.current || '—'}</span>
                              <span className="mx-1 text-gray-300">→</span>
                              <span className={midwichUpdateToggles[key] ? 'text-emerald-700 font-semibold' : 'text-gray-500'}>{v.proposed}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="flex gap-2 pt-0.5">
                        <button
                          className="text-[8px] px-2 py-0.5 bg-emerald-600 text-white rounded font-semibold"
                          onClick={async () => {
                            const updates = {};
                            for (const key of ['description', 'manufacturer', 'manufacturer_part_number', 'category']) {
                              const proposed = midwichUpdateCandidate.fields[key]?.proposed;
                              if (midwichUpdateToggles[key] && proposed) updates[key] = proposed;
                            }
                            setEditing(p => ({ ...p, ...updates }));
                            // Same as the TD SYNNEX flow: also capture the source price, so
                            // one click takes both the product detail and the Midwich cost.
                            const r = midwichUpdateCandidate.result || {};
                            const added = await addSupplier({
                              supplier_name: 'Midwich',
                              supplier_sku: r.midwich_sku,
                              unit_price: r.cost_price != null ? String(r.cost_price) : '',
                              lead_time_days: '',
                              is_preferred: false,
                              notes: [r.manufacturer_part_number, r.description].filter(Boolean).join(' — ').slice(0, 100),
                              auto_sync: true,
                            });
                            if (added) toast.success('Product updated & Midwich price added to supplier pricing');
                            setMidwichUpdateCandidate(null);
                          }}
                        >Apply selected</button>
                        <button className="text-[8px] px-2 py-0.5 border border-gray-300 rounded text-gray-500" onClick={() => setMidwichUpdateCandidate(null)}>Dismiss</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {showOneMediaSearch && (
                <OneMediaLookup
                  editing={editing}
                  onUse={(fields) => {
                    setNewSupplier(s => ({ ...s, ...fields, unit_price: fields.unit_price || s.unit_price }));
                    setShowOneMediaSearch(false);
                    setAddingSupplier(true);
                  }}
                  onApply={async (updates, fields) => {
                    setEditing(p => ({ ...p, ...updates }));
                    // As the TD SYNNEX and Midwich flows: one click takes both the
                    // product detail and the OneMedia cost.
                    const added = await addSupplier({ ...fields, lead_time_days: '', is_preferred: false });
                    if (added) toast.success('Product updated & OneMedia price added to supplier pricing');
                  }}
                />
              )}
              <div className="overflow-x-auto"><table className="w-full text-[9px]">
                <thead>
                  <tr>
                    <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold border-b bg-gray-50">Supplier</th>
                    <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold border-b bg-gray-50">SKU</th>
                    <th className="px-2 py-1 text-right text-[7px] uppercase text-gray-400 font-bold border-b bg-gray-50">Price £</th>
                    <th className="px-2 py-1 text-right text-[7px] uppercase text-gray-400 font-bold border-b bg-gray-50">Lead</th>
                    <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold border-b bg-gray-50" title="A link to the item on the supplier's site, or how to order it, e.g. Email Jane at Westcoast">How to order</th>
                    <th className="px-2 py-1 text-center text-[7px] uppercase text-gray-400 font-bold border-b bg-gray-50">Preferred</th>
                    <th className="px-2 py-1 text-center text-[7px] uppercase text-gray-400 font-bold border-b bg-gray-50">Auto-sync</th>
                    <th className="px-2 py-1 border-b bg-gray-50 w-6" />
                  </tr>
                </thead>
                <tbody>
                  {addingSupplier && (
                    <tr className="bg-gold/5 border-b border-gold/20">
                      <td className="px-2 py-1">
                        <input
                          className="form-input text-[9px]"
                          placeholder="Supplier name"
                          value={newSupplier.supplier_name}
                          onChange={e => setNewSupplier(p => ({ ...p, supplier_name: e.target.value }))}
                          list="supplier-names-list"
                          autoFocus
                        />
                        <datalist id="supplier-names-list">
                          {allSupplierNames.map(s => <option key={s.id} value={s.name} />)}
                        </datalist>
                      </td>
                      <td className="px-2 py-1"><input className="form-input text-[9px] w-20" placeholder="SKU" value={newSupplier.supplier_sku} onChange={e => setNewSupplier(p => ({ ...p, supplier_sku: e.target.value }))} /></td>
                      <td className="px-2 py-1"><input className="form-input text-[9px] w-20 text-right" type="number" step="0.01" placeholder="0.00" value={newSupplier.unit_price} onChange={e => setNewSupplier(p => ({ ...p, unit_price: e.target.value }))} /></td>
                      <td className="px-2 py-1"><input className="form-input text-[9px] w-14 text-right" type="number" min="0" placeholder="0" value={newSupplier.lead_time_days} onChange={e => setNewSupplier(p => ({ ...p, lead_time_days: e.target.value }))} /></td>
                      <td className="px-2 py-1"><input className="form-input text-[9px] min-w-[140px]" maxLength={1000} placeholder="Link, or e.g. Email Jane at…" value={newSupplier.how_to_order} onChange={e => setNewSupplier(p => ({ ...p, how_to_order: e.target.value }))} /></td>
                      <td className="px-2 py-1 text-center"><input type="checkbox" checked={newSupplier.is_preferred} onChange={e => setNewSupplier(p => ({ ...p, is_preferred: e.target.checked }))} className="w-3 h-3" /></td>
                      <td className="px-2 py-1 text-center">
                        <label className="flex items-center gap-1 text-[9px] text-gray-600 justify-center">
                          <input type="checkbox" checked={newSupplier.auto_sync} onChange={e => setNewSupplier(s => ({ ...s, auto_sync: e.target.checked }))} />
                          <span className="text-[8px] text-gray-500">Daily</span>
                        </label>
                      </td>
                      <td className="px-2 py-1">
                        <div className="flex gap-1">
                          {/* Called with no argument on purpose. addSupplier takes an
                              optional `override`, so passing the handler directly here
                              handed it the click event: `src.supplier_name` was undefined
                              and it returned early, so this button silently did nothing. */}
                          <button onClick={() => addSupplier()} disabled={savingSupplier} className="px-1.5 py-0.5 bg-emerald-600 text-white text-[8px] rounded">{savingSupplier ? '…' : 'Save'}</button>
                          <button onClick={() => setAddingSupplier(false)} className="px-1.5 py-0.5 bg-gray-200 text-gray-600 text-[8px] rounded">✕</button>
                        </div>
                      </td>
                    </tr>
                  )}
                  {suppliers.map(s => (
                    <tr key={s.id} className={`border-b border-gray-100 hover:bg-gray-50 ${s.is_preferred ? 'bg-emerald-50' : ''}`}>
                      <td className="px-2 py-1 font-medium">
                        {editingSupplier?.id === s.id && editingSupplier.field === 'supplier_name'
                          ? <input autoFocus className="form-input text-[9px]" defaultValue={s.supplier_name}
                              onBlur={e => saveSupplier(s.id, { supplier_name: e.target.value })}
                              onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingSupplier(null); }} />
                          : <span className="cursor-text" onClick={() => setEditingSupplier({ id: s.id, field: 'supplier_name' })}>{s.supplier_name}</span>}
                      </td>
                      <td className="px-2 py-1 font-mono text-gray-400">
                        <div>{s.supplier_sku || '—'}</div>
                        {s.notes && <div className="text-[7px] text-gray-300 font-sans truncate max-w-[120px]" title={s.notes}>{s.notes}</div>}
                      </td>
                      <td className="px-2 py-1 text-right font-semibold">
                        {editingSupplier?.id === s.id && editingSupplier.field === 'unit_price'
                          ? <input autoFocus className="form-input text-[9px] w-20 text-right" type="number" step="0.01" defaultValue={s.unit_price}
                              onBlur={e => saveSupplier(s.id, { unit_price: parseFloat(e.target.value) || 0 })}
                              onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingSupplier(null); }} />
                          : <span className="cursor-text" onClick={() => setEditingSupplier({ id: s.id, field: 'unit_price' })}>£{Number(s.unit_price).toFixed(2)}</span>}
                        <div className="text-[7px] text-gray-400 mt-0.5">
                          {s.auto_sync && s.last_synced_at
                            ? `checked ${daysSince(s.last_synced_at)}`
                            : s.updated_at ? `set ${daysSince(s.updated_at)}` : null}
                        </div>
                      </td>
                      <td className="px-2 py-1 text-right text-gray-500">
                        {editingSupplier?.id === s.id && editingSupplier.field === 'lead_time_days'
                          ? <input autoFocus className="form-input text-[9px] w-14 text-right" type="number" min="0" defaultValue={s.lead_time_days}
                              onBlur={e => saveSupplier(s.id, { lead_time_days: parseInt(e.target.value) || 0 })}
                              onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingSupplier(null); }} />
                          : <span className="cursor-text" onClick={() => setEditingSupplier({ id: s.id, field: 'lead_time_days' })}>{s.lead_time_days ?? 0}d</span>}
                      </td>
                      <td className="px-2 py-1 max-w-[180px]">
                        {editingSupplier?.id === s.id && editingSupplier.field === 'how_to_order'
                          ? <input autoFocus className="form-input text-[9px] min-w-[140px]" maxLength={1000} defaultValue={s.how_to_order || ''}
                              placeholder="Link, or e.g. Email Jane at…"
                              onBlur={e => { if (e.target.value.trim() !== (s.how_to_order || '')) saveSupplier(s.id, { how_to_order: e.target.value }); else setEditingSupplier(null); }}
                              onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingSupplier(null); }} />
                          : s.how_to_order
                            ? <div className="flex items-start gap-1">
                                <HowToOrder text={s.how_to_order} className="text-[9px]" />
                                <button onClick={() => setEditingSupplier({ id: s.id, field: 'how_to_order' })} className="text-[9px] text-gray-400 hover:text-navy flex-shrink-0" title="Edit how to order">✎</button>
                              </div>
                            : <button onClick={() => setEditingSupplier({ id: s.id, field: 'how_to_order' })} className="text-[7px] text-gray-400 hover:text-navy underline">Add</button>}
                      </td>
                      <td className="px-2 py-1 text-center">
                        {s.is_preferred
                          ? <span className="text-[7px] font-bold px-1 py-0.5 bg-emerald-100 text-emerald-700 rounded">✓ Preferred</span>
                          : <button onClick={() => setPreferred(s.id)} className="text-[7px] text-gray-400 hover:text-emerald-600 underline">Set</button>}
                      </td>
                      <td className="px-2 py-1 text-center">
                        {s.auto_sync ? (
                          <div className="flex items-center justify-center gap-1">
                            <span className="text-[7px] font-bold px-1 py-0.5 bg-blue-100 text-blue-700 rounded">✓</span>
                            {/* Sync-now hits the TD SYNNEX API, so it is the only supplier
                                it can refresh. Midwich rows still auto-sync, but from the
                                daily price file — offering them a button that can only come
                                back with "no on-demand sync" is worse than not offering it. */}
                            {isAdmin && s.supplier_name === 'TD SYNNEX' && (
                              <button
                                onClick={() => syncTdsynnexSupplier(s.id)}
                                disabled={syncingSupplier === s.id}
                                className="text-[10px] text-blue-500 hover:text-blue-700 disabled:opacity-40"
                                title="Sync price now"
                              >{syncingSupplier === s.id ? '…' : '↺'}</button>
                            )}
                            {isAdmin && s.supplier_name !== 'TD SYNNEX' && (
                              <span className="text-[7px] text-gray-400 cursor-help" title={`${s.supplier_name} updates from its own scheduled price file, so there is no sync-now.`}>daily</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-[7px] text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-2 py-1 text-center">
                        <button onClick={() => deleteSupplier(s.id)} className="text-red-400 hover:text-red-600 font-bold text-[11px]">×</button>
                      </td>
                    </tr>
                  ))}
                  {suppliers.length === 0 && !addingSupplier && <tr><td colSpan={8} className="text-center py-3 text-[9px] text-gray-400">No suppliers yet — add one above</td></tr>}
                </tbody>
              </table></div>
            </div>
          </div>
        </Modal>
      )}

      {showCreate && canEdit && (
        <CreateProductModal categories={categories} manufacturers={mfrOptions} allTags={allTags} onClose={() => setShowCreate(false)} onSave={create} saving={saving} defaultCommPct={defaultCommPct} />
      )}
      {showCatManager && canEdit && (
        <CategoryManagerModal categories={categories} products={products} onClose={() => { setShowCatManager(false); load(); }} onRename={renameCategory} />
      )}
      {showBundles && canEdit && (
        <BundlesManagerModal products={products} onClose={() => setShowBundles(false)} />
      )}
      {showMatching && canEdit && (
        <SupplierMatchingPanel
          onClose={() => setShowMatching(false)}
          onChanged={load}
          onEdit={async (id) => {
            // The list rows carry only what the panel shows, so open the edit
            // window from the full record. A failure is shown on the panel's row.
            const full = await api.get(`product-catalog/${id}`);
            setShowMatching(false);
            openEdit(full);
          }}
        />
      )}
    </div>
  );
}

function ProductDetailModal({ product: p, stats, loading, canEdit, defaultCommPct, onEdit, onClose }) {
  const sell = sowPrice(p, defaultCommPct);
  const { navigate } = useContext(AppContext);
  const [tab, setTab] = useState('summary');
  // Jump to Inventory → By Product, opened on this product's stock breakdown.
  const openStock = () => { onClose(); navigate('inventory', p.id, 'product'); };
  const [priceHistory, setPriceHistory]       = useState([]);
  const [sourcingHistory, setSourcingHistory] = useState([]);
  const [auditTrail, setAuditTrail]           = useState([]);
  const [sowUsage, setSowUsage]               = useState(null);
  const [loadingTab, setLoadingTab] = useState(false);

  const loadTab = async (t) => {
    setTab(t);
    if (t === 'sow-usage' && sowUsage === null) {
      setLoadingTab(true);
      api.get(`product-catalog/${p.id}/sow-usage`).then(r => { setSowUsage(r || []); setLoadingTab(false); }).catch(() => { setSowUsage([]); setLoadingTab(false); });
    }
    if (t === 'price-history' && priceHistory.length === 0) {
      setLoadingTab(true);
      api.get(`product-catalog/${p.id}/price-history`).then(r => { setPriceHistory(r || []); setLoadingTab(false); }).catch(() => setLoadingTab(false));
    }
    if (t === 'sourcing-history' && sourcingHistory.length === 0) {
      setLoadingTab(true);
      api.get(`product-catalog/${p.id}/sourcing-history`).then(r => { setSourcingHistory(r || []); setLoadingTab(false); }).catch(() => setLoadingTab(false));
    }
    if (t === 'audit-trail' && auditTrail.length === 0) {
      setLoadingTab(true);
      api.get('audit', { entity_type: 'product_catalog', entity_id: p.id, limit: 100 })
        .then(r => { setAuditTrail(r?.results || r || []); setLoadingTab(false); })
        .catch(() => setLoadingTab(false));
    }
  };

  return (
    <Modal title={p.manufacturer_part_number ? `${p.manufacturer_part_number} — Product Summary` : `${p.code} — Product Summary`} onClose={onClose} footer={
      <div className="flex gap-2">
        {canEdit && <button onClick={onEdit} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded">Edit Product</button>}
        <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Close</button>
      </div>
    }>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2 text-[9px]">
          <div className="col-span-2">
            {p.manufacturer_part_number
              ? <>
                  <div className="font-extrabold font-mono text-[15px] text-navy leading-tight">{p.manufacturer_part_number}</div>
                  <div className="text-[10px] text-gray-600 mt-0.5 leading-snug">{p.description}</div>
                </>
              : <div className="font-bold text-[11px] text-navy">{p.description}</div>
            }
            <div className="text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
              {p.manufacturer && <span className="text-[8px]">{p.manufacturer}</span>}
              <span className="bg-gray-100 px-1.5 py-0.5 rounded font-medium text-[8px]">{p.category}</span>
            </div>
          </div>
          <div>
            <div className="text-[7.5px] font-bold uppercase text-gray-400 mb-0.5">Supplier Price Range</div>
            <div className="font-semibold text-gray-800">{fmtRange(p.min_price, p.max_price)}</div>
            {p.supplier_count > 0 && <div className="text-[7px] text-gray-400">{p.supplier_count} supplier{p.supplier_count !== 1 ? 's' : ''}</div>}
          </div>
          <div>
            <div className="text-[7.5px] font-bold uppercase text-gray-400 mb-0.5">SOW Sell Price</div>
            <div className="font-semibold text-blue-600">{fmt(sell.price)}</div>
            {sell.auto && <div className="text-[7px] text-gray-400">Auto: supplier price + {commissionPctOf(p, defaultCommPct)}%</div>}
          </div>
          {sell.price != null && p.min_price != null && (
            <div>
              <div className="text-[7.5px] font-bold uppercase text-gray-400 mb-0.5">Commission</div>
              <div className={`font-bold ${sell.price - Number(p.min_price) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                £{(sell.price - Number(p.min_price)).toFixed(2)}
                {p.min_price > 0 && <span className="text-gray-400 font-normal ml-1">({((sell.price - Number(p.min_price)) / Number(p.min_price) * 100).toFixed(1)}%)</span>}
              </div>
            </div>
          )}
          <div>
            <div className="text-[7.5px] font-bold uppercase text-gray-400 mb-0.5">Created</div>
            <div className="text-gray-600">{formatDate(p.created_at)}</div>
          </div>
          <div>
            <div className="text-[7.5px] font-bold uppercase text-gray-400 mb-0.5">Price Last Modified</div>
            <div className="text-gray-600">{p.price_last_modified ? formatDate(p.price_last_modified) : '—'}</div>
          </div>
          {/* The list row carries the date; the PO it came from arrives with the stats. */}
          <div>
            <div className="text-[7.5px] font-bold uppercase text-gray-400 mb-0.5">Last Ordered</div>
            {stats?.last_order ? (
              <button type="button" onClick={() => loadTab('sourcing-history')} title="See every PO for this product" className="text-left hover:underline">
                <div className="text-gray-600">{formatDate(stats.last_order.order_date)}</div>
                <div className="text-[7px] text-gray-400">{stats.last_order.po_number}{stats.last_order.supplier_name ? ` · ${stats.last_order.supplier_name}` : ''} ›</div>
              </button>
            ) : (
              <div className="text-gray-600">{!stats && p.last_ordered ? formatDate(p.last_ordered) : '—'}</div>
            )}
          </div>
          {p.notes && (
            <div className="col-span-2">
              <div className="text-[7.5px] font-bold uppercase text-gray-400 mb-0.5">Notes</div>
              <div className="text-gray-600">{p.notes}</div>
            </div>
          )}
        </div>

        {/* What the PMO has heard from suppliers, shown to anyone who picks this
            product. Stock is on the Usage & Stock tab below. */}
        <div className="mb-3">
          <ProductSupplyNote productId={p.id} showStock={false} canWrite={canAccess('product-notes:write')} />
        </div>

        <div className="border-t border-gray-100 pt-2">
          <div className="flex gap-0 mb-2 border-b border-gray-100">
            {[['summary','Usage & Stock'],['sow-usage','Used in SOWs'],['price-history','Price History'],['sourcing-history','Sourcing History'],['audit-trail','Change Log']].map(([t, label]) => (
              <button key={t} onClick={() => loadTab(t)} className={`px-3 py-1 text-[9px] font-semibold border-b-2 -mb-px ${tab === t ? 'border-navy text-navy' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>{label}</button>
            ))}
          </div>

          {tab === 'summary' && (
            loading ? <div className="text-[9px] text-gray-400">Loading stats…</div>
            : stats ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {stats.sow_count > 0 ? (
                  <button onClick={() => loadTab('sow-usage')} title="See which SOWs use this product" className="bg-blue-50 rounded p-2 text-center hover:bg-blue-100 transition-colors cursor-pointer">
                    <div className="text-[18px] font-extrabold text-blue-700">{stats.sow_count}</div><div className="text-[7.5px] text-blue-600 font-semibold">SOWs ›</div>
                  </button>
                ) : (
                  <div className="bg-blue-50 rounded p-2 text-center"><div className="text-[18px] font-extrabold text-blue-700">{stats.sow_count}</div><div className="text-[7.5px] text-blue-600 font-semibold">SOWs</div></div>
                )}
                {stats.in_stock > 0 ? (
                  <button onClick={openStock} title="View these items in Inventory" className="bg-emerald-50 rounded p-2 text-center hover:bg-emerald-100 transition-colors cursor-pointer">
                    <div className="text-[18px] font-extrabold text-emerald-700">{stats.in_stock}</div><div className="text-[7.5px] text-emerald-600 font-semibold">In Stock ›</div>
                  </button>
                ) : (
                  <div className="bg-emerald-50 rounded p-2 text-center"><div className="text-[18px] font-extrabold text-emerald-700">{stats.in_stock}</div><div className="text-[7.5px] text-emerald-600 font-semibold">In Stock</div></div>
                )}
                {stats.ordered_count > 0 ? (
                  <button onClick={openStock} title="View incoming items in Inventory" className="bg-amber-50 rounded p-2 text-center hover:bg-amber-100 transition-colors cursor-pointer">
                    <div className="text-[18px] font-extrabold text-amber-700">{stats.ordered_count}</div><div className="text-[7.5px] text-amber-600 font-semibold">On Order ›</div><div className="text-[7px] text-gray-400">{stats.next_delivery ? formatDate(stats.next_delivery) : 'no ETD'}</div>
                  </button>
                ) : (
                  <div className="bg-amber-50 rounded p-2 text-center"><div className="text-[18px] font-extrabold text-amber-700">{stats.ordered_count}</div><div className="text-[7.5px] text-amber-600 font-semibold">On Order</div><div className="text-[7px] text-gray-400">{stats.next_delivery ? formatDate(stats.next_delivery) : 'no ETD'}</div></div>
                )}
                <div className="bg-violet-50 rounded p-2 text-center"><div className="text-[18px] font-extrabold text-violet-700">{stats.annual_qty || 0}</div><div className="text-[7.5px] text-violet-600 font-semibold">Purchased</div><div className="text-[7px] text-gray-400">last 12 months</div></div>
              </div>
            ) : <div className="text-[9px] text-gray-400">Stats unavailable</div>
          )}
          {tab === 'summary' && !loading && p.suppliers?.length > 0 && (
            <div className="mt-2"><CatalogueSuppliers suppliers={p.suppliers} /></div>
          )}

          {tab === 'sow-usage' && (
            loadingTab || sowUsage === null ? <div className="text-[9px] text-gray-400">Loading…</div>
            : sowUsage.length === 0 ? <div className="text-[9px] text-gray-400">This product isn't linked to any SOW hardware lines yet.</div>
            : (
              <div className="space-y-3">
                <SellPriceChart usage={sowUsage} catalogSell={sell.price} fmt={fmt} />
                <div className="overflow-x-auto">
                  <table className="w-full text-[9px]">
                    <thead><tr>
                      <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold border-b">Date</th>
                      <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold border-b">SOW</th>
                      <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold border-b">Sold as (SOW-specific description)</th>
                      <th className="px-2 py-1 text-right text-[7px] uppercase text-gray-400 font-bold border-b">Qty</th>
                      <th className="px-2 py-1 text-right text-[7px] uppercase text-gray-400 font-bold border-b">Unit sell</th>
                      <th className="px-2 py-1 text-right text-[7px] uppercase text-gray-400 font-bold border-b">Line sell</th>
                    </tr></thead>
                    <tbody>
                      {[...sowUsage].reverse().map((u, i) => (
                        <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="px-2 py-1 text-gray-500 whitespace-nowrap">{u.sale_date ? formatDate(u.sale_date) : '—'}</td>
                          <td className="px-2 py-1 whitespace-nowrap">
                            <a href={`#sow-detail/${u.sow_id}`} target="_blank" rel="noopener noreferrer"
                              className="font-mono font-semibold text-navy hover:underline" title={`Open ${u.sow_reference} (new tab)`}>
                              {u.sow_reference} ↗
                            </a>
                            {u.sow_status && <span className="ml-1 text-[7px] text-gray-400">{u.sow_status}</span>}
                          </td>
                          <td className="px-2 py-1 text-gray-600 max-w-[280px]">{u.item_description}</td>
                          <td className="px-2 py-1 text-right text-gray-600">{u.quantity}</td>
                          <td className="px-2 py-1 text-right font-medium text-navy whitespace-nowrap">{fmt(u.unit_sell)}</td>
                          <td className="px-2 py-1 text-right text-gray-600 whitespace-nowrap">{fmt(u.line_sell)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          )}

          {tab === 'price-history' && (
            loadingTab ? <div className="text-[9px] text-gray-400">Loading…</div>
            : priceHistory.length === 0 ? <div className="text-[9px] text-gray-400">No price changes recorded yet.</div>
            : (
              <>
              <PriceHistoryChart history={priceHistory} fmt={fmt} />
              <div className="overflow-x-auto">
                <table className="w-full text-[9px]">
                  <thead><tr>
                    <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold border-b">Supplier</th>
                    <th className="px-2 py-1 text-right text-[7px] uppercase text-gray-400 font-bold border-b">Old £</th>
                    <th className="px-2 py-1 text-right text-[7px] uppercase text-gray-400 font-bold border-b">New £</th>
                    <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold border-b">Changed</th>
                    <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold border-b">By</th>
                    <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold border-b">Note</th>
                  </tr></thead>
                  <tbody>
                    {priceHistory.map(h => {
                      const diff = Number(h.new_price) - Number(h.old_price ?? 0);
                      return (
                        <tr key={h.id} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="px-2 py-1 font-medium">{h.supplier_name}</td>
                          <td className="px-2 py-1 text-right text-gray-400">{h.old_price != null ? fmt(h.old_price) : '—'}</td>
                          <td className={`px-2 py-1 text-right font-semibold ${diff > 0 ? 'text-red-500' : diff < 0 ? 'text-emerald-600' : ''}`}>{fmt(h.new_price)}</td>
                          <td className="px-2 py-1 text-gray-500">{formatDate(h.changed_at)}</td>
                          <td className="px-2 py-1 text-gray-400">{h.changed_by || '—'}</td>
                          <td className="px-2 py-1 text-gray-400">{h.change_note || ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </>
            )
          )}

          {tab === 'sourcing-history' && (
            loadingTab ? <div className="text-[9px] text-gray-400">Loading…</div>
            : sourcingHistory.length === 0 ? <div className="text-[9px] text-gray-400">No issued purchase orders found for this product.</div>
            : (
              <>
              <div className="text-[8px] text-gray-400 mb-1">Issued POs only, not drafts or cancelled ones. A line typed in by hand counts when its part number, or its whole description, is this product's code or manufacturer part number.</div>
              <div className="overflow-x-auto">
                <table className="w-full text-[9px]">
                  <thead><tr>
                    <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold border-b">PO No.</th>
                    <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold border-b">Date</th>
                    <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold border-b">Supplier</th>
                    <th className="px-2 py-1 text-right text-[7px] uppercase text-gray-400 font-bold border-b">Qty</th>
                    <th className="px-2 py-1 text-right text-[7px] uppercase text-gray-400 font-bold border-b">Unit £</th>
                    <th className="px-2 py-1 text-right text-[7px] uppercase text-gray-400 font-bold border-b">Total £</th>
                    <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold border-b">Status</th>
                  </tr></thead>
                  <tbody>
                    {sourcingHistory.map((h, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="px-2 py-1 font-mono font-semibold text-navy">
                          {h.po_number}
                          {h.matched_on !== 'catalogue' && (
                            <span title={`Not picked from the catalogue: its ${h.matched_on === 'part_number' ? 'part number' : 'description'} matched this product`}
                              className="block w-fit mt-0.5 px-1 py-0.5 rounded text-[7px] font-sans font-medium whitespace-nowrap bg-amber-50 text-amber-700">
                              matched on {h.matched_on === 'part_number' ? 'part no.' : 'description'}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1 text-gray-500 whitespace-nowrap">{formatDate(h.order_date)}</td>
                        <td className="px-2 py-1">{h.supplier_name || '—'}</td>
                        <td className="px-2 py-1 text-right">{h.quantity}</td>
                        <td className="px-2 py-1 text-right font-semibold">{fmt(h.unit_price)}</td>
                        <td className="px-2 py-1 text-right font-semibold text-blue-600">{fmt(h.line_total)}</td>
                        <td className="px-2 py-1"><span className="px-1 py-0.5 rounded text-[7px] bg-gray-100 text-gray-600">{h.po_status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )
          )}

          {tab === 'audit-trail' && (
            loadingTab ? <div className="text-[9px] text-gray-400">Loading…</div>
            : auditTrail.length === 0 ? <div className="text-[9px] text-gray-400">No changes recorded for this product.</div>
            : (
              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                <table className="w-full text-[9px]">
                  <thead className="sticky top-0 bg-white">
                    <tr>
                      <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold border-b">When</th>
                      <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold border-b">Who</th>
                      <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold border-b">Action</th>
                      <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold border-b">Field</th>
                      <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold border-b">Old value</th>
                      <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold border-b">New value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditTrail.map((a, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="px-2 py-1 text-gray-500 whitespace-nowrap">{fmtDateTimeLong(a.timestamp)}</td>
                        <td className="px-2 py-1 font-medium">{a.person_name || '—'}</td>
                        <td className="px-2 py-1">
                          <span className={`px-1 py-0.5 rounded text-[7px] font-bold ${
                            a.action === 'created' ? 'bg-emerald-100 text-emerald-700' :
                            a.action === 'deleted' ? 'bg-red-100 text-red-700' :
                            'bg-blue-100 text-blue-700'}`}>{a.action}</span>
                        </td>
                        <td className="px-2 py-1 font-mono text-gray-500">{a.field_name || '—'}</td>
                        <td className="px-2 py-1 text-gray-400 max-w-[100px] truncate">{a.old_value ?? '—'}</td>
                        <td className="px-2 py-1 text-gray-700 max-w-[100px] truncate">{a.new_value ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      </div>
    </Modal>
  );
}

function CategoryManagerModal({ categories, products, onClose, onRename }) {
  const [renamingFrom, setRenamingFrom] = useState(null);
  const [renameTo, setRenameTo]         = useState('');
  const [saving, setSaving]             = useState(false);

  const doRename = async () => {
    if (!renameTo.trim() || renameTo.trim() === renamingFrom) return;
    if (categories.includes(renameTo.trim()) && renameTo.trim() !== renamingFrom) {
      if (!confirm(`Merge all "${renamingFrom}" products into "${renameTo.trim()}"?`)) return;
    }
    setSaving(true);
    await onRename(renamingFrom, renameTo.trim());
    setRenamingFrom(null); setRenameTo(''); setSaving(false);
  };

  return (
    <Modal title="Manage Categories" onClose={onClose} footer={
      <button onClick={onClose} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded">Done</button>
    }>
      <div className="space-y-3">
        <div className="text-[8px] text-gray-500 bg-blue-50 border border-blue-100 rounded px-2 py-1.5">
          Categories come from products. Rename here to reorganise. To create a new category, add a product with that name.
        </div>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {categories.map(cat => {
            const count = products.filter(p => p.category === cat).length;
            return (
              <div key={cat} className="flex items-center justify-between gap-2 bg-gray-50 rounded px-2 py-1">
                {renamingFrom === cat ? (
                  <>
                    <input className="form-input flex-1 text-[9px]" value={renameTo} onChange={e => setRenameTo(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') doRename(); if (e.key === 'Escape') { setRenamingFrom(null); setRenameTo(''); }}}
                      autoFocus list="rename-cats" />
                    <datalist id="rename-cats">{categories.filter(c => c !== cat).map(c => <option key={c} value={c} />)}</datalist>
                    <button onClick={doRename} disabled={saving} className="text-[8px] px-2 py-0.5 bg-navy text-white rounded">{saving ? '…' : 'OK'}</button>
                    <button onClick={() => { setRenamingFrom(null); setRenameTo(''); }} className="text-[8px] text-gray-400">✕</button>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 flex-1">
                      <span className="text-[9px] font-semibold">{cat}</span>
                      <span className="text-[7.5px] text-gray-400">{count} product{count !== 1 ? 's' : ''}</span>
                    </div>
                    <button onClick={() => { setRenamingFrom(cat); setRenameTo(cat); }} className="text-[8px] text-gold hover:underline">Rename</button>
                  </>
                )}
              </div>
            );
          })}
        </div>
        <div className="text-[8px] text-gray-400 border-t border-gray-100 pt-2">
          To add a new category: click <span className="font-semibold">+ Add Product</span> and type the new category name.
        </div>
      </div>
    </Modal>
  );
}

function CategorySelect({ categories, value, onChange }) {
  const isExisting = categories.includes(value);
  const [addingNew, setAddingNew] = useState(!isExisting && !!value);

  if (addingNew) {
    return (
      <div className="flex gap-1">
        <input
          className="form-input flex-1"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="New category name…"
          autoFocus
        />
        <button
          type="button"
          onClick={() => { setAddingNew(false); onChange(''); }}
          className="px-1.5 py-0.5 text-[8px] border border-gray-200 rounded text-gray-500 whitespace-nowrap hover:bg-gray-50"
        >← Back</button>
      </div>
    );
  }

  return (
    <select
      className="form-input"
      value={value || ''}
      onChange={e => {
        if (e.target.value === '__new__') {
          setAddingNew(true);
          onChange('');
        } else {
          onChange(e.target.value);
        }
      }}
    >
      <option value="">Select a category…</option>
      {categories.map(c => <option key={c} value={c}>{c}</option>)}
      <option value="__new__">+ Add new category…</option>
    </select>
  );
}

function ManufacturerSelect({ manufacturers, value, onChange }) {
  const isExisting = manufacturers.includes(value);
  const [addingNew, setAddingNew] = useState(!isExisting && !!value);

  if (addingNew) {
    return (
      <div className="flex gap-1">
        <input
          className="form-input flex-1"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="New manufacturer name…"
          autoFocus
        />
        <button
          type="button"
          onClick={() => { setAddingNew(false); onChange(''); }}
          className="px-1.5 py-0.5 text-[8px] border border-gray-200 rounded text-gray-500 whitespace-nowrap hover:bg-gray-50"
        >← Back</button>
      </div>
    );
  }

  return (
    <select
      className="form-input"
      value={value || ''}
      onChange={e => {
        if (e.target.value === '__new__') {
          setAddingNew(true);
          onChange('');
        } else {
          onChange(e.target.value);
        }
      }}
    >
      <option value="">Select manufacturer…</option>
      {manufacturers.map(m => <option key={m} value={m}>{m}</option>)}
      <option value="__new__">+ New manufacturer…</option>
    </select>
  );
}

// Parse the JSON tags array stored on a product (tolerant of already-parsed
// arrays, null, or malformed values).
function parseTags(p) {
  if (!p) return [];
  const t = p.tags ?? p;
  if (Array.isArray(t)) return t;
  if (!t || typeof t !== 'string') return [];
  try { const a = JSON.parse(t); return Array.isArray(a) ? a : []; } catch { return []; }
}

// Chip editor for a product's tags: shows current tags with a remove button,
// an input that adds on Enter, and one-click suggestions from tags already used
// across the catalog. Case-insensitive de-dup so "ECS" and "ecs" don't both add.
function TagEditor({ tags = [], suggestions = [], onChange }) {
  const [input, setInput] = useState('');
  const add = (raw) => {
    const v = (raw || '').trim();
    if (!v) return;
    if (!tags.some(t => t.toLowerCase() === v.toLowerCase())) onChange([...tags, v]);
    setInput('');
  };
  const remove = (t) => onChange(tags.filter(x => x !== t));
  const avail = suggestions.filter(s =>
    !tags.some(t => t.toLowerCase() === s.toLowerCase()) &&
    (!input || s.toLowerCase().includes(input.toLowerCase()))
  );
  return (
    <div>
      <div className="flex flex-wrap gap-1 mb-1">
        {tags.length === 0 && <span className="text-[9px] text-gray-400 italic">No tags yet</span>}
        {tags.map(t => (
          <span key={t} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-navy/10 text-navy text-[9px] font-semibold">
            {t}
            <button type="button" onClick={() => remove(t)} title="Remove tag" className="text-navy/50 hover:text-red-500 leading-none">×</button>
          </span>
        ))}
      </div>
      <input className="form-input text-[10px]" value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(input); } }}
        placeholder="Type a tag and press Enter (e.g. ECS, SMS, Audio Visual)…" />
      {avail.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {avail.slice(0, 12).map(s => (
            <button type="button" key={s} onClick={() => add(s)}
              className="px-1.5 py-0.5 rounded-full border border-dashed border-gray-300 text-gray-500 text-[9px] hover:border-navy hover:text-navy">+ {s}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateProductModal({ categories, manufacturers, allTags = [], onClose, onSave, saving, defaultCommPct }) {
  const [form, setForm] = useState({ description: '', category: '', manufacturer: '', manufacturer_part_number: '', sell_price: '', commission_pct: '', notes: '', tags: [] });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <Modal title="Add Product" onClose={onClose} footer={
      <>
        <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button
          onClick={() => onSave({ ...form, sell_price: form.sell_price ? parseFloat(form.sell_price) : null, commission_pct: form.commission_pct ? parseFloat(form.commission_pct) : null })}
          disabled={saving || !form.description || !form.category}
          className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40"
        >{saving ? 'Saving…' : 'Add Product'}</button>
      </>
    }>
      <div className="grid grid-cols-2 gap-2">
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Description *</label>
          <input className="form-input" value={form.description} onChange={e => set('description', e.target.value)} autoFocus />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Category *</label>
          <CategorySelect categories={categories} value={form.category} onChange={v => set('category', v)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Manufacturer</label>
          <ManufacturerSelect manufacturers={manufacturers} value={form.manufacturer} onChange={v => set('manufacturer', v)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Manufacturer Part No.</label>
          <input className="form-input font-mono" value={form.manufacturer_part_number} onChange={e => set('manufacturer_part_number', e.target.value)} placeholder="e.g. DELL-MDS19" />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">SOW Price (£)</label>
          <input className="form-input" type="number" step="0.01" value={form.sell_price} onChange={e => set('sell_price', e.target.value)} placeholder="Blank = supplier price + commission" />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Commission % <span className="text-gray-400 font-normal">(blank = {defaultCommPct}% default)</span></label>
          <input className="form-input" type="number" step="0.1" min="0" value={form.commission_pct} onChange={e => set('commission_pct', e.target.value)} placeholder={`${defaultCommPct}`} />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Notes</label>
          <input className="form-input" value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Tags</label>
          <TagEditor tags={form.tags} suggestions={allTags} onChange={v => set('tags', v)} />
        </div>
      </div>
    </Modal>
  );
}

function BundlesManagerModal({ products, onClose }) {
  const { toast } = useToast();
  const [bundles, setBundles]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selected, setSelected]         = useState(null); // bundle being edited
  const [bundleItems, setBundleItems]   = useState([]);
  const [newBundleName, setNewBundleName] = useState('');
  const [newBundleDesc, setNewBundleDesc] = useState('');
  const [creatingBundle, setCreatingBundle] = useState(false);
  const [showNewBundle, setShowNewBundle]   = useState(false);
  const [itemSearch, setItemSearch]     = useState('');
  const [addingItem, setAddingItem]     = useState(false);

  const loadBundles = () => {
    setLoading(true);
    api.get('product-bundles').then(r => { setBundles(r || []); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { loadBundles(); }, []);

  const selectBundle = async (b) => {
    setSelected(b);
    setItemSearch('');
    const full = await api.get(`product-bundles/${b.id}`).catch(() => null);
    setBundleItems(full?.items || []);
  };

  const createBundle = async () => {
    if (!newBundleName.trim()) return;
    setCreatingBundle(true);
    try {
      const b = await api.post('product-bundles', { name: newBundleName.trim(), description: newBundleDesc.trim() || null });
      setNewBundleName(''); setNewBundleDesc(''); setShowNewBundle(false);
      loadBundles();
      selectBundle(b);
    } catch (e) { toast.error(e.message); }
    setCreatingBundle(false);
  };

  const deleteBundle = async (b) => {
    if (!confirm(`Delete bundle "${b.name}"?`)) return;
    await api.delete(`product-bundles/${b.id}`);
    if (selected?.id === b.id) { setSelected(null); setBundleItems([]); }
    loadBundles();
  };

  const addItem = async (product) => {
    if (!selected) return;
    setAddingItem(true);
    try {
      const item = await api.post(`product-bundles/${selected.id}/items`, { product_catalog_id: product.id, quantity: 1, is_optional: false });
      setBundleItems(prev => [...prev, item]);
      setItemSearch('');
    } catch (e) { toast.error(e.message); }
    setAddingItem(false);
  };

  const updateItem = async (itemId, patch) => {
    await api.put(`product-bundles/${selected.id}/items/${itemId}`, patch);
    setBundleItems(prev => prev.map(i => i.id === itemId ? { ...i, ...patch } : i));
  };

  const removeItem = async (itemId) => {
    await api.delete(`product-bundles/${selected.id}/items/${itemId}`);
    setBundleItems(prev => prev.filter(i => i.id !== itemId));
    loadBundles();
  };

  const filteredProducts = itemSearch.trim()
    ? products.filter(p =>
        p.is_active &&
        !bundleItems.some(bi => bi.product_catalog_id === p.id) &&
        (p.description.toLowerCase().includes(itemSearch.toLowerCase()) ||
         p.code.toLowerCase().includes(itemSearch.toLowerCase()) ||
         (p.manufacturer || '').toLowerCase().includes(itemSearch.toLowerCase()))
      ).slice(0, 8)
    : [];

  return (
    <Modal title="Product Bundles" wide onClose={onClose} footer={
      <button onClick={onClose} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded">Done</button>
    }>
      <div className="flex gap-4 min-h-[340px]">
        {/* Left: bundle list */}
        <div className="w-48 flex-shrink-0 border-r border-gray-100 pr-3 flex flex-col gap-2">
          <button
            onClick={() => setShowNewBundle(s => !s)}
            className="px-2 py-1 text-[9px] font-semibold bg-navy text-white rounded hover:bg-navy/80 w-full"
          >+ New Bundle</button>
          {showNewBundle && (
            <div className="space-y-1.5 p-2 bg-amber-50 border border-amber-200 rounded">
              <input
                className="form-input text-[9px]"
                placeholder="Bundle name…"
                value={newBundleName}
                onChange={e => setNewBundleName(e.target.value)}
                autoFocus
                onKeyDown={e => e.key === 'Enter' && createBundle()}
              />
              <input
                className="form-input text-[9px]"
                placeholder="Description (optional)"
                value={newBundleDesc}
                onChange={e => setNewBundleDesc(e.target.value)}
              />
              <div className="flex gap-1">
                <button onClick={createBundle} disabled={creatingBundle || !newBundleName.trim()} className="px-2 py-0.5 bg-emerald-600 text-white text-[8px] rounded disabled:opacity-40">{creatingBundle ? '…' : 'Create'}</button>
                <button onClick={() => { setShowNewBundle(false); setNewBundleName(''); setNewBundleDesc(''); }} className="px-2 py-0.5 border border-gray-200 text-gray-500 text-[8px] rounded">✕</button>
              </div>
            </div>
          )}
          {loading ? (
            <div className="text-[8px] text-gray-400">Loading…</div>
          ) : bundles.length === 0 ? (
            <div className="text-[8px] text-gray-400">No bundles yet</div>
          ) : (
            <div className="space-y-1 overflow-y-auto flex-1">
              {bundles.map(b => (
                <div
                  key={b.id}
                  className={`flex items-start justify-between rounded px-2 py-1.5 cursor-pointer group ${selected?.id === b.id ? 'bg-navy text-white' : 'bg-gray-50 hover:bg-gray-100'}`}
                  onClick={() => selectBundle(b)}
                >
                  <div className="flex-1 min-w-0">
                    <div className={`text-[9px] font-semibold truncate ${selected?.id === b.id ? 'text-white' : 'text-gray-800'}`}>{b.name}</div>
                    <div className={`text-[7.5px] ${selected?.id === b.id ? 'text-white/70' : 'text-gray-400'}`}>{b.item_count} item{b.item_count !== 1 ? 's' : ''}</div>
                  </div>
                  <button
                    onClick={e => { e.stopPropagation(); deleteBundle(b); }}
                    className={`text-[11px] font-bold flex-shrink-0 opacity-0 group-hover:opacity-100 ml-1 ${selected?.id === b.id ? 'text-white/70 hover:text-white' : 'text-red-400 hover:text-red-600'}`}
                  >×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: bundle items */}
        <div className="flex-1 min-w-0">
          {!selected ? (
            <div className="flex items-center justify-center h-full text-[9px] text-gray-400">Select or create a bundle to edit its items</div>
          ) : (
            <div className="space-y-3">
              <div>
                <div className="text-[10px] font-bold text-navy">{selected.name}</div>
                {selected.description && <div className="text-[8px] text-gray-500">{selected.description}</div>}
              </div>
              <div className="overflow-x-auto"><table className="w-full text-[9px]">
                <thead>
                  <tr>
                    <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold border-b bg-gray-50">Product</th>
                    <th className="px-2 py-1 text-center text-[7px] uppercase text-gray-400 font-bold border-b bg-gray-50">Qty</th>
                    <th className="px-2 py-1 text-center text-[7px] uppercase text-gray-400 font-bold border-b bg-gray-50">Optional</th>
                    <th className="px-2 py-1 border-b bg-gray-50 w-6" />
                  </tr>
                </thead>
                <tbody>
                  {bundleItems.map(item => (
                    <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-2 py-1">
                        <span className="text-[8px] font-mono font-bold text-navy mr-2">{item.code}</span>
                        <span className="text-[9px]">{item.description}</span>
                        {item.manufacturer && <span className="text-[8px] text-gray-400 ml-2">{item.manufacturer}</span>}
                      </td>
                      <td className="px-2 py-1 text-center">
                        <input
                          type="number" min="1"
                          className="form-input text-[9px] w-12 text-center"
                          value={item.quantity}
                          onChange={e => updateItem(item.id, { quantity: parseInt(e.target.value) || 1, is_optional: item.is_optional })}
                        />
                      </td>
                      <td className="px-2 py-1 text-center">
                        <input
                          type="checkbox"
                          checked={!!item.is_optional}
                          onChange={e => updateItem(item.id, { quantity: item.quantity, is_optional: e.target.checked })}
                          className="w-3 h-3"
                        />
                      </td>
                      <td className="px-2 py-1 text-center">
                        <button onClick={() => removeItem(item.id)} className="text-red-400 hover:text-red-600 text-[11px] font-bold">×</button>
                      </td>
                    </tr>
                  ))}
                  {bundleItems.length === 0 && (
                    <tr><td colSpan={4} className="text-center py-4 text-[9px] text-gray-400">No items yet — search below to add products</td></tr>
                  )}
                </tbody>
              </table></div>
              <div className="relative">
                <input
                  className="form-input text-[9px]"
                  placeholder="Search products to add to bundle…"
                  value={itemSearch}
                  onChange={e => setItemSearch(e.target.value)}
                  disabled={addingItem}
                />
                {filteredProducts.length > 0 && (
                  <div className="absolute z-20 left-0 right-0 top-full mt-0.5 bg-white border border-gray-200 rounded shadow-lg max-h-40 overflow-y-auto">
                    {filteredProducts.map(p => (
                      <div key={p.id}
                        className="px-2 py-1.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100 flex items-center justify-between"
                        onClick={() => addItem(p)}
                      >
                        <div>
                          <span className="text-[8px] font-mono font-bold text-navy mr-2">{p.code}</span>
                          <span className="text-[9px]">{p.description}</span>
                          {p.manufacturer && <span className="text-[8px] text-gray-400 ml-2">{p.manufacturer}</span>}
                        </div>
                        <span className="text-[7.5px] text-gray-400 flex-shrink-0 ml-2">{p.category}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {bundleItems.some(i => i.is_optional) && (
                <div className="text-[7.5px] text-gray-400 bg-gray-50 rounded px-2 py-1">
                  Optional items are suggested but not automatically added when the bundle is used on a PO.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
